import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  BarChart3,
  Car,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  FileText,
  Filter,
  Gift,
  Percent,
  PlaneLanding,
  PlaneTakeoff,
  Route,
  Sparkles,
  Tag,
  Package,
  Truck,
} from 'lucide-react';
import MainLayout from '../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../shell/backofficeSidebar';
import { useAuth } from '../../../../context/AuthContext';
import { useCurrency } from '../../../../context/CurrencyContext';
import { PaginatedListLoading } from '../../../../components/ui/PaginatedListLoading';
import { formatOrderReference } from '../../../../utils/formatOrderReference';
import { openPdfFromBase64Payload } from '../../../../utils/openPdfFromBase64Payload';
import OrderFulfillmentPanel from '../components/OrderFulfillmentPanel';
import {
  USE_CARRIER_FULFILLMENT,
  estimateOrderWeightLbs,
  isInternationalDestination,
} from '../../fulfillmentConfig';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';
const PAGE_SIZE = 10;

type StaffDiscountAudit = {
  totalDiscountUsd: number;
  pickDiscountUsd: number;
  promoCodeDiscountUsd: number;
  referralDiscountUsd: number;
  retentionDiscountUsd: number;
  retentionDiscountPercent: number | null;
  referralAttributionId: string | null;
  promotion: {
    id: string;
    code: string;
    description: string | null;
    discountType: string;
    fixedDiscountUsd: number | null;
    percentOff: number | null;
  } | null;
};

type SalesOrder = {
  id: string;
  status: string;
  isPreorderOrder?: boolean;
  preorderDepositUsd?: number | string;
  preorderRemainingUsd?: number | string;
  /** Set when status is PREORDER_AWAITING_RELEASE and inventory cannot cover the order */
  preorderReleaseBlockedReason?: string | null;
  totalUsd: number;
  subtotalUsd?: number;
  deliveryFeeUsd?: number;
  discountUsd?: number;
  referralDiscountUsd?: number;
  createdAt: string;
  deliveredAt?: string | null;
  customerId: string | null;
  /** Set when the customer deleted their account; staff workflow is frozen. */
  customerAccountDetachedAt?: string | null;
  staffDiscountAudit?: StaffDiscountAudit;
  addressSnapshot?: {
    fullName?: string;
    phone?: string;
    street?: string;
    city?: string;
    postalCode?: string;
    country?: string;
    notes?: string;
  };
  events?: Array<{
    id: string;
    type: string;
    fromStatus?: string | null;
    toStatus?: string | null;
    createdAt: string;
  }>;
  incidents?: Array<{
    id: string;
    reason: 'UNREACHABLE' | 'DAMAGED';
    summary?: string | null;
    openedAt: string;
    resolution?: 'FAULT_EMPLOYEE' | 'FAULT_CUSTOMER' | null;
    resolvedAt?: string | null;
    openedBy?: { id: string; email: string } | null;
    resolvedBy?: { id: string; email: string } | null;
  }>;
  losses?: Array<{
    id: string;
    productId: string;
    lotId?: string | null;
    quantityLbs: number;
    reason: string;
    unitCostAtLoss?: number | null;
    notes?: string | null;
    createdAt: string;
    createdById?: string | null;
  }>;
  addressVerification?: {
    status?: 'PENDING' | 'VERIFIED' | 'FAILED';
    checkedAt?: string | null;
    error?: string | null;
  } | null;
  fulfillmentMethod?: 'IN_HOUSE' | 'FEDEX' | 'UPS' | null;
  carrierServiceCode?: string | null;
  carrierServiceName?: string | null;
  customer?: {
    id: string;
    email: string;
    customerProfile?: {
      fullName?: string | null;
      phone?: string | null;
      street?: string | null;
      city?: string | null;
      postalCode?: string | null;
      country?: string | null;
      drivingDurationSeconds?: number | null;
      operationalDurationSeconds?: number | null;
      drivingDistanceMeters?: number | null;
      flightDurationSeconds?: number | null;
      isAbroadDelivery?: boolean | null;
      abroadRoutePlan?: {
        mode?: string;
        totalDurationSeconds?: number;
        totalDistanceMeters?: number;
        originAirport?: { code?: string; name?: string };
        destinationAirport?: { code?: string; name?: string };
        legs?: Array<{
          type?: 'DRIVE_TO_AIRPORT' | 'FLIGHT' | 'DRIVE_TO_DESTINATION';
          label?: string;
          durationSeconds?: number;
          distanceMeters?: number;
          from?: string;
          to?: string;
        }>;
      } | null;
      drivingComputedAt?: string | null;
      drivingRouteStatus?: string | null;
    } | null;
  };
  items: Array<{
    id: string;
    packageId?: string | null;
    packageNameSnapshot?: string | null;
    productId?: string | null;
    productNameSnapshot: string;
    qty: number;
    unit: string;
    originalUnitPriceUsdSnapshot?: number | string;
    productDiscountPercentSnapshot?: number | string;
    unitPriceUsdSnapshot?: number | string;
    lineTotalUsd?: number | string;
    lineDiscountUsd?: number | string;
    product?: {
      id: string;
      name: string;
      baseUnit?: string;
      category?: {
        department?: { id?: string; name?: string; slug?: string } | null;
      } | null;
    } | null;
    stockReservations?: Array<{
      id: string;
      qtyLbs: number;
      productId: string;
      product?: {
        id: string;
        name: string;
        baseUnit?: string;
        category?: {
          department?: { id?: string; name?: string; slug?: string } | null;
        } | null;
      } | null;
    }>;
    allocations?: Array<{
      id: string;
      qtyLbs: number;
      lot?: {
        id: string;
        productId?: string;
        product?: {
          id: string;
          name: string;
          baseUnit?: string;
          category?: {
            department?: { id?: string; name?: string; slug?: string } | null;
          } | null;
        } | null;
      } | null;
    }>;
    package?: {
      id: string;
      items?: Array<{
        id: string;
        qty: number;
        unit: string;
        productId?: string;
        product?: {
          id: string;
          name: string;
          baseUnit?: string;
          category?: {
            department?: { id?: string; name?: string; slug?: string } | null;
          } | null;
        } | null;
      }>;
    } | null;
  }>;
  deliverySpeed?: string;
  deliveryBatch?: {
    id: string;
    areaLabel: string;
    name: string | null;
    windowStart?: string;
    windowEnd?: string;
    status: string;
  } | null;
  assignedShippingAgentId?: string | null;
  assignedShippingAgent?: {
    id: string;
    email: string;
    employeeProfile?: { fullName?: string | null } | null;
  } | null;
  payment?: {
    id: string;
    stripePaymentIntentId: string;
    stripeStatus: string;
    amountMinor: number;
    currency: string;
  } | null;
  balancePayments?: Array<{
    id: string;
    stripePaymentIntentId: string;
    stripeStatus: string;
    amountMinor: number;
    currency: string;
  }>;
  refundRequests?: Array<{
    id: string;
    reason: string;
    reasonText?: string | null;
    status:
      | 'REQUESTED'
      | 'APPROVED'
      | 'REJECTED'
      | 'WAITING_RETURN'
      | 'RETURN_RECEIVED'
      | 'REFUND_PROCESSING'
      | 'DONE'
      | 'FAILED';
    requestNote?: string | null;
    rejectionReason?: string | null;
    approvedAt?: string | null;
    rejectedAt?: string | null;
    createdAt: string;
    createdBy?: { id: string; email: string } | null;
    reviewedBy?: { id: string; email: string } | null;
    evidences?: Array<{
      id: string;
      imageUrl: string;
      note?: string | null;
      createdAt: string;
    }>;
    refunds?: Array<{
      id: string;
      status: string;
      amountMinor: number;
      currency: string;
      createdAt: string;
      stripeRefundId?: string | null;
    }>;
  }>;
};

function preorderRemainingUsdNumber(order: SalesOrder): number {
  return Number(order.preorderRemainingUsd ?? 0);
}

function preorderLastBalancePayment(order: SalesOrder) {
  const rows = order.balancePayments ?? [];
  return rows.length ? rows[rows.length - 1] : null;
}

function formatDrivingEta(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);
  return parts.join(' ');
}

function metersToMiles(meters: number): string {
  const miles = meters / 1609.344;
  return `${miles.toFixed(miles >= 10 ? 1 : 2)} mi`;
}

function formatProcessingTime(startIso?: string | null, endIso?: string | null): string {
  if (!startIso) return '—';
  const start = new Date(startIso).getTime();
  const end = new Date(endIso || new Date().toISOString()).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return '—';
  const seconds = Math.round((end - start) / 1000);
  return formatDrivingEta(seconds);
}

function getDeliveredAtFromOrder(order: SalesOrder): string | null {
  if (order.deliveredAt) return order.deliveredAt;
  const ev = (order.events || [])
    .filter((e) => e.toStatus === 'DELIVERED')
    .sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    )[0];
  return ev?.createdAt || null;
}

type OpenBatchRow = {
  id: string;
  name: string | null;
  areaLabel: string;
  status: string;
  deliverySpeed?: 'STANDARD' | 'EXPRESS';
};

const STATUSES = [
  'PENDING', 'PAID', 'SHIPPED', 'UNREACHABLE', 'DAMAGED', 'INVESTIGATING',
  'FAULT_EMPLOYEE', 'FAULT_CUSTOMER', 'RESHIP_PENDING', 'INSUFFICIENT_STOCK_PENDING',
  'RESHIPPED', 'DELIVERED', 'CLOSED', 'REFUNDED', 'CANCELLED',
] as const;
const FLOW = ['PENDING', 'PAID', 'SHIPPED', 'DELIVERED', 'CLOSED'] as const;

function hasSettledStripePayment(order: SalesOrder): boolean {
  return String(order.payment?.stripeStatus || '').toLowerCase() === 'succeeded';
}

/** UI fallback: visually treat paid Stripe orders as PAID even if status sync lags. */
function progressDisplayStatus(order: SalesOrder): string {
  if (order.status === 'PENDING' && hasSettledStripePayment(order)) {
    return 'PAID';
  }
  return order.status;
}

/** Orders that can be linked to an open delivery batch (first outing or reship outing). */
function canAssignOrderToDeliveryBatchStatus(status: string): boolean {
  return status === 'PAID' || status === 'RESHIP_PENDING';
}

/**
 * Orders that can have a shipping agent directly assigned. Broader than the
 * batch-assign window because an agent may be assigned even after dispatch
 * (a one-off where the package never went through batch dispatch).
 */
function canAssignOrderToShippingAgentStatus(status: string): boolean {
  return (
    status === 'PAID' ||
    status === 'RESHIP_PENDING' ||
    status === 'SHIPPED' ||
    status === 'RESHIPPED'
  );
}

function orderStatusAccentClass(status: string): string {
  if (status === 'PAID' || status === 'RESHIP_PENDING') return 'border-l-emerald-500';
  if (status === 'SHIPPED' || status === 'RESHIPPED') return 'border-l-sky-500';
  if (status === 'DELIVERED') return 'border-l-indigo-500';
  if (status === 'CLOSED' || status === 'REFUNDED') return 'border-l-slate-400';
  if (status === 'CANCELLED') return 'border-l-rose-500';
  return 'border-l-amber-400';
}

/** Prominent but compact banner for the shipping service the customer chose at checkout. */
function CarrierShippingBanner({
  serviceName,
  serviceCode,
  t,
  compact = false,
}: {
  serviceName?: string | null;
  serviceCode?: string | null;
  t: TFunction;
  compact?: boolean;
}) {
  const label = serviceName || serviceCode || 'FedEx';
  return (
    <div
      className={[
        'flex items-center gap-2 rounded-md border border-sky-200 bg-sky-50/80',
        compact ? 'px-2 py-1.5' : 'px-2.5 py-2',
      ].join(' ')}
    >
      <div
        className={[
          'flex shrink-0 items-center justify-center rounded bg-[#4D148C] text-white',
          compact ? 'h-6 w-6' : 'h-7 w-7',
        ].join(' ')}
        aria-hidden
      >
        <Package className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} strokeWidth={2.25} />
      </div>
      <div className="min-w-0">
        <p className="whitespace-nowrap text-[9px] font-semibold uppercase tracking-wide text-sky-700">
          {t('backoffice.orders_customer_shipping_choice', {
            defaultValue: 'Customer shipping choice',
          })}
        </p>
        <p className={['truncate font-bold text-slate-900', compact ? 'text-xs' : 'text-sm'].join(' ')}>
          {label}
        </p>
      </div>
    </div>
  );
}

const INCIDENT_BRANCH_STATUSES = [
  'UNREACHABLE',
  'DAMAGED',
  'INVESTIGATING',
  'FAULT_EMPLOYEE',
  'FAULT_CUSTOMER',
  'RESHIP_PENDING',
  'INSUFFICIENT_STOCK_PENDING',
  'RESHIPPED',
] as const;

function orderLifecycleRank(status: string): number {
  if (status === 'CANCELLED') return -1;
  if (status === 'PENDING') return 0;
  if (status === 'PAID') return 1;
  if (status === 'SHIPPED') return 2;
  if (status === 'UNREACHABLE' || status === 'DAMAGED') return 2.45;
  if (status === 'INVESTIGATING') return 2.5;
  if (status === 'FAULT_EMPLOYEE' || status === 'FAULT_CUSTOMER') return 2.55;
  if (status === 'RESHIP_PENDING' || status === 'INSUFFICIENT_STOCK_PENDING') return 2.65;
  if (status === 'RESHIPPED') return 2.75;
  if (status === 'DELIVERED') return 3;
  if (status === 'CLOSED' || status === 'REFUNDED') return 4;
  if (
    [
      'REFUND_REQUESTED',
      'WAITING_RETURN',
      'RETURN_RECEIVED',
      'REFUND_PROCESSING',
      'REFUND_REJECTED',
    ].includes(status)
  ) {
    return 2.6;
  }
  return 2;
}

function branchStepIndex(status: string): number {
  if (status === 'UNREACHABLE' || status === 'DAMAGED') return 0;
  if (status === 'INVESTIGATING') return 1;
  if (status === 'FAULT_EMPLOYEE' || status === 'FAULT_CUSTOMER') return 2;
  if (status === 'RESHIP_PENDING' || status === 'INSUFFICIENT_STOCK_PENDING') return 3;
  if (status === 'RESHIPPED') return 4;
  return -1;
}

const ORDER_REFUND_STATUSES = [
  'REFUND_REQUESTED',
  'WAITING_RETURN',
  'RETURN_RECEIVED',
  'REFUND_PROCESSING',
  'REFUND_REJECTED',
  'REFUNDED',
] as const;

function refundRequestIsTracked(order: SalesOrder): boolean {
  const rr = order.refundRequests?.[0];
  if (!rr) return false;
  return ['REQUESTED', 'APPROVED', 'WAITING_RETURN', 'RETURN_RECEIVED', 'REFUND_PROCESSING', 'DONE'].includes(
    rr.status,
  );
}

function shouldShowIncidentOrderProgress(order: SalesOrder): boolean {
  const s = order.status;
  if ((INCIDENT_BRANCH_STATUSES as readonly string[]).includes(s)) return true;
  if ((ORDER_REFUND_STATUSES as readonly string[]).includes(s)) return true;
  if (order.incidents && order.incidents.length > 0) return true;
  if (refundRequestIsTracked(order)) return true;
  if (s === 'DELIVERED' || s === 'CLOSED') {
    const ev = order.events || [];
    if (ev.some((e) => e.toStatus === 'RESHIPPED' || e.toStatus === 'UNREACHABLE' || e.toStatus === 'DAMAGED'))
      return true;
  }
  return false;
}

function shouldShowPreorderOrderProgress(order: SalesOrder): boolean {
  return Boolean(order.isPreorderOrder) && !shouldShowIncidentOrderProgress(order);
}

type OrderViewScope = 'ACTIVE' | 'INCIDENTS' | 'RESOLUTION' | 'REFUND_QUEUE' | 'ARCHIVE' | 'ALL';

function matchesOrderView(order: SalesOrder, orderView: OrderViewScope): boolean {
  if (orderView === 'ALL') return true;
  if (orderView === 'ARCHIVE') {
    return order.status === 'CLOSED' || order.status === 'CANCELLED' || order.status === 'REFUNDED';
  }
  if (orderView === 'INCIDENTS') {
    return ['UNREACHABLE', 'DAMAGED', 'INVESTIGATING'].includes(order.status);
  }
  if (orderView === 'RESOLUTION') {
    return ['FAULT_EMPLOYEE', 'FAULT_CUSTOMER', 'RESHIP_PENDING', 'INSUFFICIENT_STOCK_PENDING'].includes(
      order.status,
    );
  }
  if (orderView === 'REFUND_QUEUE') {
    return (
      (order.refundRequests?.length || 0) > 0
      && !['DONE', 'REJECTED', 'FAILED'].includes(order.refundRequests?.[0]?.status || '')
    );
  }
  return !['CLOSED', 'CANCELLED', 'REFUNDED'].includes(order.status);
}

/** Linear pre-order lifecycle (deposit → release → balance → standard fulfillment). */
function preorderActiveStepIndex(status: string): number {
  switch (status) {
    case 'PENDING':
      return 0;
    case 'PREORDER_AWAITING_RELEASE':
      return 1;
    case 'PREORDER_BALANCE_DUE':
      return 2;
    case 'PAID':
      return 3;
    case 'SHIPPED':
      return 4;
    case 'DELIVERED':
      return 5;
    case 'CLOSED':
    case 'REFUNDED':
      return 6;
    case 'CANCELLED':
      return -1;
    default:
      return 3;
  }
}

function preorderStepMode(
  status: string,
  stepIdx: number,
  resolvedDeliveredAt?: string | null,
): NodeVisual {
  if (status === 'CANCELLED') return 'pending';
  const active = preorderActiveStepIndex(status);
  if (active < 0) return 'pending';
  if (status === 'CLOSED' && stepIdx === 5 && !resolvedDeliveredAt) return 'skipped';
  if (stepIdx < active) return 'done';
  if (stepIdx === active) return 'current';
  return 'pending';
}

const PREORDER_FLOW_KEYS = [
  'deposit',
  'release',
  'balance',
  'paid',
  'shipped',
  'delivered',
  'closed',
] as const;

function PreorderOrderProgress({
  status,
  t,
  resolvedDeliveredAt,
}: {
  status: string;
  t: TFunction;
  resolvedDeliveredAt?: string | null;
}) {
  const isCancelled = status === 'CANCELLED';
  const resolved = resolvedDeliveredAt ?? null;

  const pill = (label: string, mode: NodeVisual, stepKey: (typeof PREORDER_FLOW_KEYS)[number]) => {
    const complete = mode === 'done' || mode === 'current';
    return (
      <div
        className={[
          'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 sm:gap-2 sm:px-3 sm:py-1.5',
          complete
            ? 'border-indigo-300 bg-indigo-50/95'
            : mode === 'skipped'
              ? 'border border-dashed border-amber-400/90 bg-amber-50/90'
              : 'border-slate-200 bg-slate-50/95',
        ].join(' ')}
      >
        <div
          className={[
            'h-2.5 w-2.5 shrink-0 rounded-full border sm:h-3 sm:w-3',
            complete
              ? 'border-indigo-600 bg-indigo-600'
              : mode === 'skipped'
                ? 'border-amber-500 bg-amber-100'
                : 'border-slate-400 bg-white',
          ].join(' ')}
        />
        <div
          className={[
            'whitespace-nowrap text-[10px] font-bold uppercase tracking-wide sm:text-xs',
            complete ? 'text-indigo-950' : mode === 'skipped' ? 'text-amber-950' : 'text-slate-600',
          ].join(' ')}
        >
          {label}
          {mode === 'skipped' && stepKey === 'delivered' ? (
            <span className="ml-1 font-normal normal-case text-amber-800/90">
              ({t('backoffice.orders_progress_delivered_skipped', { defaultValue: 'n/a' })})
            </span>
          ) : null}
        </div>
      </div>
    );
  };

  const connector = (leftComplete: boolean) => (
    <div
      className={[
        'h-0.5 w-full min-w-[0.5rem] rounded-full sm:h-1',
        leftComplete ? 'bg-indigo-500' : 'bg-slate-300/80',
      ].join(' ')}
    />
  );

  const labelFor = (key: (typeof PREORDER_FLOW_KEYS)[number]) => {
    switch (key) {
      case 'deposit':
        return t('backoffice.orders_preorder_step_deposit', { defaultValue: 'Deposit paid' });
      case 'release':
        return t('backoffice.orders_preorder_step_release', { defaultValue: 'Awaiting release' });
      case 'balance':
        return t('backoffice.orders_preorder_step_balance', { defaultValue: 'Balance due' });
      case 'paid':
        return t('backoffice.orders_preorder_step_paid', { defaultValue: 'Fully paid' });
      case 'shipped':
        return t('backoffice.orders_preorder_step_shipped', { defaultValue: 'Shipped' });
      case 'delivered':
        return t('backoffice.orders_preorder_step_delivered', { defaultValue: 'Delivered' });
      case 'closed':
        return t('backoffice.orders_preorder_step_closed', { defaultValue: 'Closed' });
      default:
        return key;
    }
  };

  return (
    <div className="rounded-xl border border-indigo-200/90 bg-gradient-to-r from-indigo-50/50 via-white to-slate-50/80 p-3 shadow-sm sm:p-3.5">
      <div className="mb-2.5 flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-800 sm:text-[11px]">
          {t('backoffice.orders_progress_title', { defaultValue: 'Order progress' })}
        </div>
        <div className="rounded-full bg-indigo-100/90 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-indigo-950 ring-1 ring-indigo-200/80">
          {t('backoffice.orders_preorder_track_badge', { defaultValue: 'Pre-order' })}
        </div>
      </div>
      <p className="mb-2 text-[10px] text-slate-600 sm:text-[11px]">
        {t('backoffice.orders_preorder_track_hint', {
          defaultValue:
            'Deposit first, then staff releases when stock is available. Customer pays the remaining balance before fulfillment.',
        })}
      </p>
      <div className="min-w-0 overflow-x-auto pb-1 [-webkit-overflow-scrolling:touch]">
        <div className="flex w-max max-w-none items-center gap-1.5 sm:gap-2">
          {PREORDER_FLOW_KEYS.map((key, idx) => {
            const mode = preorderStepMode(status, idx, resolved);
            const segDone =
              mode === 'done' || mode === 'current' || mode === 'skipped';
            return (
              <React.Fragment key={key}>
                <div className="flex shrink-0 items-center">{pill(labelFor(key), mode, key)}</div>
                {idx !== PREORDER_FLOW_KEYS.length - 1 ? (
                  <div className="flex min-h-[3px] min-w-2 flex-1 basis-0 items-center self-center sm:min-h-[4px]">
                    {connector(segDone)}
                  </div>
                ) : null}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      {isCancelled ? (
        <div className="mt-2 inline-flex rounded-full bg-rose-100 px-2.5 py-1 text-xs font-semibold text-rose-700 ring-1 ring-rose-200">
          CANCELLED
        </div>
      ) : null}
    </div>
  );
}

/** Reship leg step 0…4 while active; 5 = finished reship leg (merge to Delivered); -1 = not on reship diagram leg */
function effectiveReshipBranchStep(order: SalesOrder): number {
  const s = order.status;
  const r = branchStepIndex(s);
  if (r >= 0) return r;
  if (s !== 'DELIVERED' && s !== 'CLOSED' && s !== 'REFUNDED') return -1;
  const ev = order.events || [];
  if (ev.some((e) => e.toStatus === 'RESHIPPED')) return 5;
  if (order.incidents?.length && (s === 'DELIVERED' || s === 'CLOSED')) {
    return 2;
  }
  return -1;
}

type NodeVisual = 'done' | 'current' | 'pending' | 'skipped';

function refundBranchNodeState(order: SalesOrder): NodeVisual {
  const s = order.status;
  if (s === 'REFUNDED') return 'done';
  const rr = order.refundRequests?.[0];
  if (rr?.status === 'DONE') return 'done';
  if (
    (ORDER_REFUND_STATUSES as readonly string[]).includes(s) &&
    s !== 'REFUND_REJECTED' &&
    s !== 'REFUNDED'
  ) {
    return 'current';
  }
  if (
    rr &&
    ['REQUESTED', 'APPROVED', 'WAITING_RETURN', 'RETURN_RECEIVED', 'REFUND_PROCESSING'].includes(rr.status)
  ) {
    return 'current';
  }
  if (s === 'REFUND_REJECTED') return 'pending';
  // Reship-merge path: refund column was not used
  if (effectiveReshipBranchStep(order) >= 5) return 'skipped';
  return 'pending';
}

function reshipBranchColumnState(order: SalesOrder, col: 0 | 1 | 2 | 4 | 5): NodeVisual {
  const logical = col <= 2 ? col : col === 4 ? 3 : 4;
  const rs = effectiveReshipBranchStep(order);
  if (rs < 0) return 'pending';
  if (rs === 5) return 'done';
  if (rs === 2 && (col === 4 || col === 5)) {
    const ev = order.events || [];
    if (!ev.some((e) => e.toStatus === 'RESHIPPED')) return 'skipped';
  }
  if (logical < rs) return 'done';
  if (logical === rs) return 'current';
  return 'pending';
}

const ALLOWED_TRANSITIONS: Record<string, string[]> = {
  PREORDER_AWAITING_RELEASE: ['CANCELLED'],
  PREORDER_BALANCE_DUE: ['PAID', 'CANCELLED'],
  PENDING: ['PAID', 'CANCELLED'],
  PAID: ['SHIPPED'],
  SHIPPED: ['DELIVERED', 'UNREACHABLE', 'DAMAGED'],
  UNREACHABLE: ['INVESTIGATING'],
  DAMAGED: ['INVESTIGATING'],
  INVESTIGATING: ['FAULT_EMPLOYEE', 'FAULT_CUSTOMER'],
  FAULT_EMPLOYEE: ['RESHIP_PENDING'],
  RESHIP_PENDING: ['RESHIPPED', 'INSUFFICIENT_STOCK_PENDING'],
  INSUFFICIENT_STOCK_PENDING: ['RESHIP_PENDING'],
  RESHIPPED: ['DELIVERED', 'UNREACHABLE', 'DAMAGED'],
  FAULT_CUSTOMER: ['CLOSED'],
  DELIVERED: ['CLOSED'],
  CLOSED: [],
  REFUNDED: [],
  CANCELLED: [],
};

const STATUS_ACTION_STYLE: Record<string, string> = {
  PREORDER_BALANCE_DUE:
    'border-indigo-300 bg-indigo-50 text-indigo-800 shadow-sm hover:border-indigo-400 hover:bg-indigo-100',
  PAID: 'border-emerald-600 bg-emerald-600 text-white shadow-sm hover:bg-emerald-700',
  SHIPPED: 'border-blue-600 bg-blue-600 text-white shadow-sm hover:bg-blue-700',
  DELIVERED: 'border-violet-600 bg-violet-600 text-white shadow-sm hover:bg-violet-700',
  INSUFFICIENT_STOCK_PENDING: 'border-amber-500 bg-amber-500 text-white shadow-sm hover:bg-amber-600',
  CLOSED: 'border-slate-900 bg-slate-900 text-white shadow-sm hover:bg-black',
  REFUNDED: 'border-rose-600 bg-rose-600 text-white shadow-sm hover:bg-rose-700',
  CANCELLED: 'border-slate-500 bg-slate-500 text-white shadow-sm hover:bg-slate-600',
};

function fulfillmentMainStepMode(
  status: string,
  resolvedDeliveredAt: string | null | undefined,
  step: (typeof FLOW)[number],
): NodeVisual {
  const isCancelled = status === 'CANCELLED';
  const rank = orderLifecycleRank(status);
  if (isCancelled) return 'pending';
  if (step === 'PENDING') {
    return status === 'PENDING' ? 'current' : 'done';
  }
  if (step === 'PAID') {
    if (status === 'PAID') return 'current';
    return rank >= 1 ? 'done' : 'pending';
  }
  if (step === 'SHIPPED') {
    if (status === 'SHIPPED') return 'current';
    return rank >= 2 ? 'done' : 'pending';
  }
  if (step === 'DELIVERED') {
    if (status === 'DELIVERED') return 'done';
    if (status === 'CLOSED' && !resolvedDeliveredAt) return 'skipped';
    return rank >= 3 ? 'done' : 'pending';
  }
  if (step === 'CLOSED') {
    if (status === 'CLOSED' || status === 'REFUNDED') return 'done';
    return rank >= 4 ? 'done' : 'pending';
  }
  return 'pending';
}

/** Single-line progress for standard fulfillment orders only */
function SimpleOrderProgress({
  status,
  t,
  resolvedDeliveredAt,
  compact = false,
}: {
  status: string;
  t: TFunction;
  resolvedDeliveredAt?: string | null;
  compact?: boolean;
}) {
  const isCancelled = status === 'CANCELLED';
  const resolved = resolvedDeliveredAt ?? null;
  const mainMode = (step: (typeof FLOW)[number]) =>
    fulfillmentMainStepMode(status, resolved, step);

  const pill = (label: string, mode: NodeVisual) => {
    const complete = mode === 'done' || mode === 'current';
    const isCurrent = mode === 'current';
    return (
      <div
        className={[
          'inline-flex min-w-0 items-center gap-1 rounded-full border',
          compact ? 'px-1.5 py-0.5' : 'px-2 py-0.5',
          complete
            ? 'border-emerald-400 bg-emerald-50'
            : mode === 'skipped'
              ? 'border-dashed border-amber-400 bg-amber-50'
              : 'border-amber-300 bg-amber-50',
          isCurrent ? 'ring-1 ring-emerald-400' : '',
        ].join(' ')}
      >
        <div
          className={[
            'shrink-0 rounded-full border-2',
            compact ? 'h-1.5 w-1.5' : 'h-2 w-2',
            complete ? 'border-emerald-600 bg-emerald-600' : 'border-amber-500 bg-amber-100',
          ].join(' ')}
        />
        <div
          className={[
            'font-bold uppercase tracking-wide',
            compact ? 'text-[9px]' : 'text-[10px]',
            complete ? 'text-emerald-900' : 'text-amber-950',
          ].join(' ')}
        >
          {label}
          {mode === 'skipped' ? (
            <span className="ml-0.5 font-normal normal-case text-amber-800/90">
              ({t('backoffice.orders_progress_delivered_skipped', { defaultValue: 'n/a' })})
            </span>
          ) : null}
        </div>
      </div>
    );
  };

  const connector = (leftDone: boolean) => (
    <div
      className={[
        'h-px w-full min-w-[0.35rem] rounded-full',
        leftDone ? 'bg-emerald-500' : 'bg-amber-300',
      ].join(' ')}
    />
  );

  return (
    <div className={compact ? 'min-w-0' : 'rounded-md border border-slate-200 bg-slate-50/40 p-2'}>
      <div className={['flex flex-wrap items-baseline justify-between gap-1', compact ? 'mb-1' : 'mb-1.5'].join(' ')}>
        <div className="text-[9px] font-bold uppercase tracking-wide text-slate-600">
          {t('backoffice.orders_progress_title', { defaultValue: 'Order progress' })}
        </div>
        {USE_CARRIER_FULFILLMENT ? (
          <div className="text-[9px] font-semibold uppercase tracking-wide text-sky-700">
            {t('backoffice.orders_progress_carrier_subtitle', { defaultValue: 'FedEx shipping' })}
          </div>
        ) : (
          <div className="text-[9px] font-semibold uppercase tracking-wide text-slate-500">
            {t('backoffice.orders_progress_simple_subtitle', { defaultValue: 'Standard fulfillment' })}
          </div>
        )}
      </div>
      <div className="min-w-0 overflow-x-auto">
        <div className="flex w-full min-w-0 items-center gap-1">
          {FLOW.map((s, idx) => (
            <React.Fragment key={s}>
              <div className="flex shrink-0 items-center">{pill(s, mainMode(s))}</div>
              {idx !== FLOW.length - 1 ? (
                <div className="flex min-h-[2px] min-w-2 flex-1 basis-0 items-center self-center">
                  {connector(mainMode(s) === 'done' || mainMode(s) === 'current')}
                </div>
              ) : null}
            </React.Fragment>
          ))}
        </div>
      </div>
      {isCancelled ? (
        <div className="mt-1 inline-flex rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold text-rose-700 ring-1 ring-rose-200">
          CANCELLED
        </div>
      ) : null}
    </div>
  );
}

/** Incident diagram: only completed (green) vs not completed (warning amber). */
function svgIncidentNodeStyle(v: NodeVisual): {
  fill: string;
  stroke: string;
  sw: number;
  dash?: string;
} {
  if (v === 'done') {
    return { fill: '#ecfdf5', stroke: '#059669', sw: 2 };
  }
  if (v === 'skipped') {
    return { fill: '#f1f5f9', stroke: '#64748b', sw: 2, dash: '4 3' };
  }
  return { fill: '#fffbeb', stroke: '#d97706', sw: 2 };
}

const SVG_STROKE_DONE = '#10b981';
const SVG_STROKE_WARN = '#f59e0b';

/** Branch + merge diagram for incident, reship, and refund workflows */
function IncidentOrderProgressDiagram({
  order,
  t,
  resolvedDeliveredAt,
}: {
  order: SalesOrder;
  t: TFunction;
  resolvedDeliveredAt?: string | null;
}) {
  const status = order.status;
  const isCancelled = status === 'CANCELLED';
  const resolved = resolvedDeliveredAt ?? null;
  const mainMode = (step: (typeof FLOW)[number]) =>
    fulfillmentMainStepMode(status, resolved, step);

  const mainCx = [40, 168, 296, 612, 748];
  const mainY = 30;
  const branchCx = [72, 176, 280, 384, 488, 592];
  const branchY = 102;
  const r = 11;
  const br = r;

  const issueSub =
    status === 'UNREACHABLE'
      ? t('backoffice.orders_progress_unreachable_short', { defaultValue: 'Unreachable' })
      : status === 'DAMAGED'
        ? t('backoffice.orders_progress_damaged_short', { defaultValue: 'Damaged' })
        : '';
  const faultSub =
    status === 'FAULT_EMPLOYEE'
      ? t('backoffice.orders_progress_fault_employee', { defaultValue: 'Staff fault' })
      : status === 'FAULT_CUSTOMER'
        ? t('backoffice.orders_progress_fault_customer', { defaultValue: 'Customer fault' })
        : '';

  const branchLabels = [
    t('backoffice.orders_progress_issue', { defaultValue: 'Issue' }),
    t('backoffice.orders_progress_investigating', { defaultValue: 'Investigate' }),
    t('backoffice.orders_progress_resolution', { defaultValue: 'Resolution' }),
    t('backoffice.orders_progress_refund_step', { defaultValue: 'Refund' }),
    t('backoffice.orders_progress_reship_prep', { defaultValue: 'Reship queue' }),
    t('backoffice.orders_progress_out_again', { defaultValue: 'Out (reship)' }),
  ];

  const refundSt = refundBranchNodeState(order);
  const colModes: NodeVisual[] = [
    reshipBranchColumnState(order, 0),
    reshipBranchColumnState(order, 1),
    reshipBranchColumnState(order, 2),
    refundSt,
    reshipBranchColumnState(order, 4),
    reshipBranchColumnState(order, 5),
  ];

  const subs = [issueSub, '', faultSub, '', '', ''];

  const stemComplete = mainMode('SHIPPED') === 'done';
  const mergeComplete =
    mainMode('DELIVERED') === 'done' || effectiveReshipBranchStep(order) >= 5;

  const stemX = mainCx[2];
  const stemJoinY = branchY;
  const lastBrX = branchCx[5];
  const delX = mainCx[3];

  return (
    <div className="rounded-xl border border-amber-200/60 bg-gradient-to-b from-amber-50/25 via-white to-white p-2.5 shadow-sm ring-1 ring-slate-200/80">
      <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-800">
          {t('backoffice.orders_progress_title', { defaultValue: 'Order progress' })}
        </div>
        <div className="rounded-full bg-amber-100/90 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-950 ring-1 ring-amber-300/70">
          {t('backoffice.orders_progress_incident_badge', {
            defaultValue: 'Incident & refund workflow',
          })}
        </div>
      </div>
      <div className="w-full overflow-x-auto">
        <svg
          width="784"
          height="148"
          viewBox="0 0 784 148"
          className="mx-auto max-w-full"
          aria-label={t('backoffice.orders_progress_title', { defaultValue: 'Order progress' })}
        >
          {mainCx.slice(0, -1).map((left, i) => {
            const right = mainCx[i + 1];
            const doneSeg = mainMode(FLOW[i]) === 'done';
            const shippedToDeliveredSeg = FLOW[i] === 'SHIPPED';
            return (
              <line
                key={`mseg-${FLOW[i]}`}
                x1={left + br}
                y1={mainY}
                x2={right - br}
                y2={mainY}
                stroke={
                  shippedToDeliveredSeg
                    ? SVG_STROKE_WARN
                    : doneSeg
                      ? SVG_STROKE_DONE
                      : SVG_STROKE_WARN
                }
                strokeWidth="2.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            );
          })}
          <path
            d={`M ${stemX} ${mainY + br} L ${stemX} ${stemJoinY}`}
            fill="none"
            stroke={stemComplete ? SVG_STROKE_DONE : SVG_STROKE_WARN}
            strokeWidth="2.25"
            strokeLinecap="round"
          />
          {branchCx.slice(0, -1).map((_, i) => {
            const x1 = branchCx[i] + br;
            const x2 = branchCx[i + 1] - br;
            const segDone =
              colModes[i] === 'done' || colModes[i] === 'skipped';
            return (
              <line
                key={`bseg-${i}`}
                x1={x1}
                y1={stemJoinY}
                x2={x2}
                y2={stemJoinY}
                stroke={segDone ? SVG_STROKE_DONE : SVG_STROKE_WARN}
                strokeWidth="2.25"
                strokeLinecap="round"
              />
            );
          })}
          <path
            d={`M ${lastBrX + br - 1} ${branchY} C ${lastBrX + 85} ${branchY - 8}, ${delX - 85} ${mainY + 8}, ${delX - br} ${mainY}`}
            fill="none"
            stroke={mergeComplete ? SVG_STROKE_DONE : SVG_STROKE_WARN}
            strokeWidth="2.25"
            strokeLinecap="round"
          />
          {mainCx.map((cx, i) => {
            const steps: (typeof FLOW)[number][] = [...FLOW];
            const m = mainMode(steps[i]);
            const { fill, stroke, sw, dash } = svgIncidentNodeStyle(m);
            const lab = steps[i];
            return (
              <g key={lab}>
                <circle
                  cx={cx}
                  cy={mainY}
                  r={br}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth={sw}
                  strokeDasharray={dash}
                />
                <text
                  x={cx}
                  y={mainY + br + 11}
                  textAnchor="middle"
                  fill="#1e293b"
                  style={{ fontSize: '8px', fontWeight: 700 }}
                >
                  {lab}
                </text>
                {m === 'skipped' && lab === 'DELIVERED' ? (
                  <text
                    x={cx}
                    y={mainY + br + 21}
                    textAnchor="middle"
                    fill="#9a3412"
                    style={{ fontSize: '7px', fontWeight: 600 }}
                  >
                    {t('backoffice.orders_progress_delivered_skipped', { defaultValue: 'n/a' })}
                  </text>
                ) : null}
              </g>
            );
          })}
          {branchCx.map((cx, i) => {
            const { fill, stroke, sw, dash } = svgIncidentNodeStyle(colModes[i]);
            return (
              <g key={`b-${i}`}>
                <circle
                  cx={cx}
                  cy={branchY}
                  r={br - 1}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth={sw}
                  strokeDasharray={dash}
                />
                <text
                  x={cx}
                  y={branchY + br + 9}
                  textAnchor="middle"
                  fill="#1e293b"
                  style={{ fontSize: '7.5px', fontWeight: 700 }}
                >
                  {branchLabels[i]}
                </text>
                {subs[i] ? (
                  <text
                    x={cx}
                    y={branchY + br + 19}
                    textAnchor="middle"
                    fill="#57534e"
                    style={{ fontSize: '7px', fontWeight: 600 }}
                  >
                    {subs[i]}
                  </text>
                ) : null}
              </g>
            );
          })}
        </svg>
      </div>
      {isCancelled ? (
        <div className="mt-2 inline-flex rounded-full bg-rose-100 px-2.5 py-1 text-xs font-semibold text-rose-700 ring-1 ring-rose-200">
          CANCELLED
        </div>
      ) : null}
    </div>
  );
}

function OrderDiscountAuditBlock({
  order,
  formatPrice,
  t,
}: {
  order: SalesOrder;
  formatPrice: (n: number) => string;
  t: TFunction;
}) {
  const audit = order.staffDiscountAudit;
  if (!audit) return null;
  const showRetention = audit.retentionDiscountUsd > 1e-9;
  const showReferral = audit.referralDiscountUsd > 1e-9 || Boolean(audit.referralAttributionId);
  const showPromo =
    audit.promotion != null || audit.promoCodeDiscountUsd > 1e-9;
  const showPick = audit.pickDiscountUsd > 1e-9;
  if (!showRetention && !showReferral && !showPromo && !showPick) return null;

  return (
    <div className="mt-3 rounded-lg border border-violet-200 bg-violet-50/80 px-3 py-2.5 text-[11px] leading-snug text-violet-950 shadow-sm">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-violet-800">
        {t('backoffice.orders_discount_audit_title', { defaultValue: 'Discounts & references' })}
      </div>
      <ul className="mt-2 space-y-1.5">
        {showRetention ? (
          <li className="flex flex-wrap items-start gap-2">
            <Percent className="mt-0.5 h-3.5 w-3.5 shrink-0 text-violet-600" aria-hidden />
            <span>
              <span className="font-semibold text-violet-950">
                {t('backoffice.orders_discount_retention', { defaultValue: 'Account retention (stay offer)' })}
              </span>
              {audit.retentionDiscountPercent != null && audit.retentionDiscountPercent > 1e-9 ? (
                <span className="text-slate-600">
                  {' · '}
                  {audit.retentionDiscountPercent % 1 === 0
                    ? `${audit.retentionDiscountPercent}%`
                    : `${audit.retentionDiscountPercent.toFixed(1)}%`}
                  {t('backoffice.orders_discount_off_subtotal', { defaultValue: ' off subtotal (applied)' })}
                </span>
              ) : null}
              {audit.retentionDiscountUsd > 1e-9 ? (
                <>
                  {' — '}
                  <span className="tabular-nums font-medium">{formatPrice(audit.retentionDiscountUsd)}</span>
                </>
              ) : null}
            </span>
          </li>
        ) : null}
        {showReferral ? (
          <li className="flex flex-wrap items-start gap-2">
            <Gift className="mt-0.5 h-3.5 w-3.5 shrink-0 text-violet-600" aria-hidden />
            <span>
              <span className="font-semibold text-violet-950">
                {t('backoffice.orders_discount_referral', { defaultValue: 'Referral welcome' })}
              </span>
              {audit.referralDiscountUsd > 1e-9 ? (
                <>
                  {' — '}
                  <span className="tabular-nums font-medium">{formatPrice(audit.referralDiscountUsd)}</span>
                  <span className="text-slate-600"> {t('backoffice.orders_discount_off_order', { defaultValue: 'off order' })}</span>
                </>
              ) : null}
              {audit.referralAttributionId ? (
                <>
                  {' · '}
                  <span className="text-slate-600">
                    {t('backoffice.orders_discount_attribution_id', { defaultValue: 'Attribution' })}
                  </span>{' '}
                  <code className="rounded bg-white/90 px-1 py-0.5 font-mono text-[10px] text-slate-900 ring-1 ring-violet-200/80">
                    {audit.referralAttributionId}
                  </code>
                </>
              ) : null}
            </span>
          </li>
        ) : null}
        {showPromo ? (
          <li className="flex flex-wrap items-start gap-2">
            <Tag className="mt-0.5 h-3.5 w-3.5 shrink-0 text-violet-600" aria-hidden />
            <span>
              <span className="font-semibold text-violet-950">
                {t('backoffice.orders_discount_promo', { defaultValue: 'Promotion code' })}
              </span>
              {audit.promotion ? (
                <>
                  {' — '}
                  <code className="rounded bg-white/90 px-1 py-0.5 font-mono text-[10px] text-slate-900 ring-1 ring-violet-200/80">
                    {audit.promotion.code}
                  </code>
                  {' · '}
                  <span className="text-slate-600">
                    {t('backoffice.orders_discount_promo_id', { defaultValue: 'Promotion ID' })}
                  </span>{' '}
                  <code className="rounded bg-white/90 px-1 py-0.5 font-mono text-[10px] text-slate-900 ring-1 ring-violet-200/80">
                    {audit.promotion.id}
                  </code>
                  {audit.promotion.discountType === 'PERCENT' ? (
                    <span className="text-slate-600">
                      {' · '}
                      {t('backoffice.orders_discount_type_percent', { defaultValue: 'Percent' })}{' '}
                      {audit.promotion.percentOff != null ? `${audit.promotion.percentOff}%` : '—'}
                    </span>
                  ) : (
                    <span className="text-slate-600">
                      {' · '}
                      {t('backoffice.orders_discount_type_fixed', { defaultValue: 'Fixed USD' })}
                      {audit.promotion.fixedDiscountUsd != null
                        ? ` ${formatPrice(audit.promotion.fixedDiscountUsd)}`
                        : ''}
                    </span>
                  )}
                </>
              ) : null}
              {audit.promoCodeDiscountUsd > 1e-9 ? (
                <>
                  {' · '}
                  <span className="tabular-nums font-medium">{formatPrice(audit.promoCodeDiscountUsd)}</span>
                  <span className="text-slate-600"> {t('backoffice.orders_discount_off_subtotal', { defaultValue: 'off subtotal (applied)' })}</span>
                </>
              ) : null}
            </span>
          </li>
        ) : null}
        {showPick ? (
          <li className="flex flex-wrap items-start gap-2">
            <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-violet-600" aria-hidden />
            <span>
              <span className="font-semibold text-violet-950">
                {t('backoffice.orders_discount_checkout_marketing', { defaultValue: 'Checkout marketing (line)' })}
              </span>
              {' — '}
              <span className="tabular-nums font-medium">{formatPrice(audit.pickDiscountUsd)}</span>
              <span className="text-slate-600"> {t('backoffice.orders_discount_line_total', { defaultValue: 'line discounts' })}</span>
            </span>
          </li>
        ) : null}
      </ul>
      {typeof order.subtotalUsd === 'number' && typeof order.deliveryFeeUsd === 'number' ? (
        <p className="mt-2 border-t border-violet-200/80 pt-2 text-[10px] leading-relaxed text-violet-900/85">
          {t('backoffice.orders_discount_audit_math', {
            defaultValue:
              'Subtotal {{sub}} + delivery {{del}} − order discounts {{disc}} = charged total {{tot}}',
            sub: formatPrice(order.subtotalUsd),
            del: formatPrice(order.deliveryFeeUsd),
            disc: formatPrice(audit.totalDiscountUsd),
            tot: formatPrice(Number(order.totalUsd)),
          })}
        </p>
      ) : null}
    </div>
  );
}

export default function WorkspaceOrdersPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { authorizedFetch, auth } = useAuth();
  const globalAuthorizedFetch = React.useCallback(
    (input: RequestInfo | URL, init: RequestInit = {}) => {
      const headers = new Headers(init.headers || {});
      // Orders workspace is intentionally global (not department-scoped).
      headers.set('x-department-slug', '');
      return authorizedFetch(input, { ...init, headers });
    },
    [authorizedFetch],
  );
  const { currency, formatPrice, formatMajorInCurrency } = useCurrency();

  const permissions = auth.permissions || [];
  const canOrderRead = permissions.includes('ORDER:READ');
  const canOrderUpdate = permissions.includes('ORDER:UPDATE');
  const canViewDetails = permissions.includes('ORDER_DETAIL:READ');
  const canIncidentManage = permissions.includes('ORDER_INCIDENT:UPDATE');
  const canResolutionManage = permissions.includes('ORDER_RESOLUTION:UPDATE');
  const canReship = permissions.includes('ORDER_RESHIP:CREATE');
  const canViewStats = permissions.includes('ORDER_STATS:READ') || canOrderRead;
  const canDeliveryBatch = permissions.includes('DELIVERY_BATCH:UPDATE');
  const canVerifyStripePayment =
    permissions.includes('ORDER:UPDATE') && permissions.includes('PAYMENT:READ');
  const [orders, setOrders] = useState<SalesOrder[]>([]);
  const [openBatches, setOpenBatches] = useState<OpenBatchRow[]>([]);
  const [batchListFilter, setBatchListFilter] = useState('');
  const [batchModalOrderId, setBatchModalOrderId] = useState<string | null>(null);
  const [batchModalPick, setBatchModalPick] = useState('');
  const [carrierModalOrderId, setCarrierModalOrderId] = useState<string | null>(null);
  const [batchAssignBusy, setBatchAssignBusy] = useState<string | null>(null);
  const [shippingAgents, setShippingAgents] = useState<
    Array<{ id: string; email: string; fullName: string | null }>
  >([]);
  const [agentPick, setAgentPick] = useState<Record<string, string>>({});
  const [agentAssignBusy, setAgentAssignBusy] = useState<string | null>(null);
  const [receiptDownloadingId, setReceiptDownloadingId] = useState<string | null>(null);
  const [shippingLabelDownloadingId, setShippingLabelDownloadingId] = useState<string | null>(null);
  const [shippingLabelModal, setShippingLabelModal] = useState<{
    orderId: string;
    packageCount: number;
    packageIndex: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [countryFilter, setCountryFilter] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [statsOpen, setStatsOpen] = useState(false);
  const [orderView, setOrderView] = useState<OrderViewScope>(
    (() => {
      const raw = searchParams.get('view') || 'ACTIVE';
      if (
        raw === 'INCIDENTS' ||
        raw === 'RESOLUTION' ||
        raw === 'REFUND_QUEUE' ||
        raw === 'ARCHIVE' ||
        raw === 'ALL'
      )
        return raw;
      return 'ACTIVE';
    })(),
  );
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [expandedArchivedOrderIds, setExpandedArchivedOrderIds] = useState<Record<string, true>>({});
  /** Per order: whether pre-order card payment table is expanded (`undefined` = collapsed). */
  const [preorderPaymentsExpanded, setPreorderPaymentsExpanded] = useState<Record<string, boolean>>({});
  const [statusUpdating, setStatusUpdating] = useState<{ orderId: string; next: string } | null>(null);
  const [stripePiDraft, setStripePiDraft] = useState<Record<string, string>>({});
  const [page, setPage] = useState(1);
  const [faultModalOrder, setFaultModalOrder] = useState<SalesOrder | null>(null);
  const [faultAdjustments, setFaultAdjustments] = useState<Record<string, string>>({});
  const [faultModalError, setFaultModalError] = useState<string | null>(null);
  const [faultStockByProductId, setFaultStockByProductId] = useState<Record<string, number>>({});
  const [faultStockLoading, setFaultStockLoading] = useState(false);
  const [actionConfirm, setActionConfirm] = useState<{
    title: string;
    message: string;
    confirmLabel: string;
  } | null>(null);
  const [reshipLossModal, setReshipLossModal] = useState<{
    orderId: string;
    deliveryLossAmountMajor: string;
    returnToStoreFeeMajor: string;
    carrier: string;
    invoiceRef: string;
  } | null>(null);
  const [packageDetailsModal, setPackageDetailsModal] = useState<{
    packageCode: string;
    packageName: string;
    departmentLabel: string;
    qtyLabel: string;
    puLabel: string;
    discountLabel: string;
    totalLabel: string;
    rows: Array<{ name: string; qtyPerBundleLbs: number; qtyLbs: number }>;
  } | null>(null);
  const actionConfirmRef = React.useRef<null | (() => void)>(null);
  const [expandedItemsByOrder, setExpandedItemsByOrder] = useState<Record<string, boolean>>({});
  const [stats, setStats] = useState<{
    total: number;
    byStatus: Record<string, number>;
    byCountry: Record<string, number>;
    countryPerformance: Array<{
      country: string;
      totalOrders: number;
      incidentOrderCount: number;
      incidentRate: number;
      faultEmployeeRate: number;
      avgResolutionHours: number;
    }>;
    groups: { processing: number; incidents: number; conflictResolution: number; archive: number };
    preorders?: {
      awaitingRelease: number;
      balanceDue: number;
      open: number;
      depositCollectedUsd: number;
      remainingBalanceUsd: number;
    };
  } | null>(null);
  const refreshInFlightRef = React.useRef(false);
  const versionRef = React.useRef<string | null>(null);

  const packageBreakdownByItem = React.useCallback(
    (item: SalesOrder['items'][number]) => {
      const toLbs = (qty: number, unit?: string) => {
        const u = String(unit || '').toUpperCase();
        if (u === 'KG') return qty * 2.20462;
        return qty;
      };
      const byProduct = new Map<string, { name: string; qtyLbs: number }>();
      const reservationRows = item.stockReservations || [];
      if (reservationRows.length > 0) {
        for (const r of reservationRows) {
          const key = String(r.productId || r.product?.id || '');
          if (!key) continue;
          const prev = byProduct.get(key);
          byProduct.set(key, {
            name: r.product?.name || 'Item',
            qtyLbs: Number(r.qtyLbs || 0) + Number(prev?.qtyLbs || 0),
          });
        }
      } else {
        const allocRows = item.allocations || [];
        if (allocRows.length > 0) {
          for (const a of allocRows) {
            const key = String(a.lot?.productId || a.lot?.product?.id || '');
            if (!key) continue;
            const prev = byProduct.get(key);
            byProduct.set(key, {
              name: a.lot?.product?.name || 'Item',
              qtyLbs: Number(a.qtyLbs || 0) + Number(prev?.qtyLbs || 0),
            });
          }
        } else {
          const packageItems = item.package?.items || [];
          for (const p of packageItems) {
            const key = String(p.productId || p.product?.id || '');
            if (!key) continue;
            const prev = byProduct.get(key);
            byProduct.set(key, {
              name: p.product?.name || 'Item',
              qtyLbs:
                toLbs(Number(p.qty || 0), p.unit) * Number(item.qty || 0) +
                Number(prev?.qtyLbs || 0),
            });
          }
        }
      }
      return Array.from(byProduct.values());
    },
    [],
  );

  const departmentsForOrderItem = React.useCallback((item: SalesOrder['items'][number]) => {
    const values = new Set<string>();
    const collect = (department?: { name?: string; slug?: string } | null) => {
      const label = String(department?.name || department?.slug || '').trim();
      if (label) values.add(label);
    };
    collect(item.product?.category?.department);
    for (const r of item.stockReservations || []) {
      collect(r.product?.category?.department);
    }
    for (const a of item.allocations || []) {
      collect(a.lot?.product?.category?.department);
    }
    for (const p of item.package?.items || []) {
      collect(p.product?.category?.department);
    }
    return Array.from(values);
  }, []);

  const packageBreakdownWithIdsByItem = React.useCallback(
    (item: SalesOrder['items'][number]) => {
      const toLbs = (qty: number, unit?: string) => {
        const u = String(unit || '').toUpperCase();
        if (u === 'KG') return qty * 2.20462;
        return qty;
      };
      const byProduct = new Map<string, { productId: string; name: string; qtyLbs: number }>();
      const reservationRows = item.stockReservations || [];
      if (reservationRows.length > 0) {
        for (const r of reservationRows) {
          const key = String(r.productId || r.product?.id || '');
          if (!key) continue;
          const prev = byProduct.get(key);
          byProduct.set(key, {
            productId: key,
            name: r.product?.name || item.productNameSnapshot || 'Item',
            qtyLbs: Number(r.qtyLbs || 0) + Number(prev?.qtyLbs || 0),
          });
        }
      } else {
        const allocRows = item.allocations || [];
        if (allocRows.length > 0) {
          for (const a of allocRows) {
            const key = String(a.lot?.productId || a.lot?.product?.id || '');
            if (!key) continue;
            const prev = byProduct.get(key);
            byProduct.set(key, {
              productId: key,
              name: a.lot?.product?.name || item.productNameSnapshot || 'Item',
              qtyLbs: Number(a.qtyLbs || 0) + Number(prev?.qtyLbs || 0),
            });
          }
        } else {
          const packageItems = item.package?.items || [];
          for (const p of packageItems) {
            const key = String(p.productId || p.product?.id || '');
            if (!key) continue;
            const prev = byProduct.get(key);
            byProduct.set(key, {
              productId: key,
              name: p.product?.name || item.productNameSnapshot || 'Item',
              qtyLbs:
                toLbs(Number(p.qty || 0), p.unit) * Number(item.qty || 0) +
                Number(prev?.qtyLbs || 0),
            });
          }
        }
      }
      return Array.from(byProduct.values());
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    if (!faultModalOrder) {
      setFaultStockByProductId({});
      setFaultStockLoading(false);
      return;
    }
    const productIds = new Set<string>();
    for (const it of faultModalOrder.items) {
      for (const row of packageBreakdownWithIdsByItem(it)) {
        if (row.productId) productIds.add(row.productId);
      }
    }
    if (productIds.size === 0) {
      setFaultStockByProductId({});
      setFaultStockLoading(false);
      return;
    }
    setFaultStockLoading(true);
    void (async () => {
      const next: Record<string, number> = {};
      await Promise.all(
        [...productIds].map(async (id) => {
          try {
            const res = await globalAuthorizedFetch(`${API_BASE}/catalog/products/${encodeURIComponent(id)}`);
            if (!res.ok) return;
            const p = (await res.json()) as { currentStockLbs?: number };
            next[id] = Number(p.currentStockLbs ?? 0);
          } catch {
            // ignore missing stock row
          }
        }),
      );
      if (!cancelled) {
        setFaultStockByProductId(next);
        setFaultStockLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [faultModalOrder, globalAuthorizedFetch, packageBreakdownWithIdsByItem]);

  const faultReshipStockWarnings = useMemo(() => {
    if (!faultModalOrder) return [] as Array<{ productId: string; name: string; neededLbs: number; availableLbs: number }>;
    const neededByProduct = new Map<string, { name: string; neededLbs: number }>();
    for (const it of faultModalOrder.items) {
      const currentQty = Number(it.qty || 0);
      const adjustedQty = Number.parseFloat(faultAdjustments[it.id] ?? String(currentQty));
      if (!Number.isFinite(adjustedQty)) continue;
      const damagedQty = Math.max(0, currentQty - adjustedQty);
      if (damagedQty <= 1e-9) continue;
      const breakdown = packageBreakdownWithIdsByItem(it);
      const ratio = currentQty > 0 ? damagedQty / currentQty : 0;
      for (const row of breakdown) {
        const neededLbs = Number(row.qtyLbs || 0) * ratio;
        if (!Number.isFinite(neededLbs) || neededLbs <= 0) continue;
        const prev = neededByProduct.get(row.productId);
        neededByProduct.set(row.productId, {
          name: row.name,
          neededLbs: neededLbs + Number(prev?.neededLbs || 0),
        });
      }
    }
    const warnings: Array<{ productId: string; name: string; neededLbs: number; availableLbs: number }> = [];
    for (const [productId, row] of neededByProduct.entries()) {
      const availableLbs = Number(faultStockByProductId[productId] ?? 0);
      if (availableLbs + 1e-9 < row.neededLbs) {
        warnings.push({
          productId,
          name: row.name,
          neededLbs: row.neededLbs,
          availableLbs,
        });
      }
    }
    return warnings;
  }, [faultAdjustments, faultModalOrder, faultStockByProductId, packageBreakdownWithIdsByItem]);

  const load = React.useCallback(async (opts?: { silent?: boolean }) => {
    const silent = !!opts?.silent;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const url = new URL(`${API_BASE}/sales/orders`);
      if (statusFilter) url.searchParams.set('status', statusFilter);
      if (searchQuery.trim()) url.searchParams.set('q', searchQuery.trim());
      if (countryFilter.trim()) url.searchParams.set('country', countryFilter.trim());
      if (fromDate) {
        url.searchParams.set('from', `${fromDate}T00:00:00.000Z`);
      }
      if (toDate) {
        url.searchParams.set('to', `${toDate}T23:59:59.999Z`);
      }
      const res = await globalAuthorizedFetch(url.toString());
      if (!res.ok) throw new Error('Unable to load orders.');
      const data = (await res.json()) as SalesOrder[];
      setOrders(data);
    } catch (e) {
      if (!silent) {
        setError(e instanceof Error ? e.message : 'Unable to load orders.');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [globalAuthorizedFetch, statusFilter, fromDate, toDate, searchQuery, countryFilter]);

  const fetchOrdersVersion = React.useCallback(async () => {
    const url = new URL(`${API_BASE}/sales/orders/version`);
    if (statusFilter) url.searchParams.set('status', statusFilter);
    if (searchQuery.trim()) url.searchParams.set('q', searchQuery.trim());
    if (countryFilter.trim()) url.searchParams.set('country', countryFilter.trim());
    if (fromDate) url.searchParams.set('from', `${fromDate}T00:00:00.000Z`);
    if (toDate) url.searchParams.set('to', `${toDate}T23:59:59.999Z`);

    const res = await globalAuthorizedFetch(url.toString());
    if (!res.ok) {
      throw new Error('Unable to load order version.');
    }
    const data = (await res.json()) as { version?: string };
    return data.version || '0:0';
  }, [globalAuthorizedFetch, statusFilter, fromDate, toDate, searchQuery, countryFilter]);

  useEffect(() => {
    const raw = searchParams.get('view') || 'ACTIVE';
    const next =
      raw === 'INCIDENTS' ||
      raw === 'RESOLUTION' ||
      raw === 'REFUND_QUEUE' ||
      raw === 'ARCHIVE' ||
      raw === 'ALL'
        ? raw
        : 'ACTIVE';
    setOrderView((prev) => (prev === next ? prev : next));
  }, [searchParams]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('view', orderView);
      return next;
    });
  }, [orderView, setSearchParams]);

  useEffect(() => {
    setPage(1);
  }, [orderView, statusFilter, searchQuery, countryFilter, fromDate, toDate]);

  const loadStats = React.useCallback(async (opts?: { silent?: boolean }) => {
    const silent = !!opts?.silent;
    if (!canViewStats) return;
    try {
      const url = new URL(`${API_BASE}/sales/orders/stats`);
      if (searchQuery.trim()) url.searchParams.set('q', searchQuery.trim());
      if (countryFilter.trim()) url.searchParams.set('country', countryFilter.trim());
      if (fromDate) url.searchParams.set('from', `${fromDate}T00:00:00.000Z`);
      if (toDate) url.searchParams.set('to', `${toDate}T23:59:59.999Z`);
      const res = await globalAuthorizedFetch(url.toString());
      if (!res.ok) return;
      const data = await res.json();
      setStats(data);
    } catch {
      if (!silent) {
        // Ignore stats failures to keep page usable
      }
    }
  }, [canViewStats, fromDate, toDate, searchQuery, countryFilter, globalAuthorizedFetch]);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  useEffect(() => {
    if (!canDeliveryBatch) return;
    void (async () => {
      try {
        const res = await globalAuthorizedFetch(`${API_BASE}/sales/delivery-batches?status=OPEN`);
        if (!res.ok) return;
        setOpenBatches((await res.json()) as OpenBatchRow[]);
      } catch {
        setOpenBatches([]);
      }
    })();
  }, [canDeliveryBatch, globalAuthorizedFetch]);

  useEffect(() => {
    if (!canDeliveryBatch) return;
    void (async () => {
      try {
        const res = await globalAuthorizedFetch(
          `${API_BASE}/sales/delivery-batches/shipping-agents`,
        );
        if (!res.ok) return;
        setShippingAgents(
          (await res.json()) as Array<{
            id: string;
            email: string;
            fullName: string | null;
          }>,
        );
      } catch {
        setShippingAgents([]);
      }
    })();
  }, [canDeliveryBatch, globalAuthorizedFetch]);

  const filteredOpenBatches = useMemo(() => {
    const q = batchListFilter.trim().toLowerCase();
    if (!q) return openBatches;
    return openBatches.filter((b) =>
      `${b.areaLabel} ${b.name ?? ''}`.toLowerCase().includes(q),
    );
  }, [openBatches, batchListFilter]);

  const openBatchesBySpeed = useMemo(() => {
    return filteredOpenBatches.reduce<Record<string, OpenBatchRow[]>>((acc, b) => {
      const key = b.deliverySpeed === 'EXPRESS' ? 'EXPRESS' : 'STANDARD';
      (acc[key] ||= []).push(b);
      return acc;
    }, {});
  }, [filteredOpenBatches]);

  const assignOrderToBatch = async (orderId: string, batchIdOverride?: string) => {
    const o = orders.find((x) => x.id === orderId);
    const batchId = (batchIdOverride ?? batchModalPick ?? o?.deliveryBatch?.id ?? '').trim();
    if (!batchId) return;
    setBatchAssignBusy(orderId);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/sales/delivery-batches/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId, batchId }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message || 'assign');
      }
      setBatchModalOrderId(null);
      await load({ silent: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'assign');
    } finally {
      setBatchAssignBusy(null);
    }
  };

  const assignOrderToAgent = async (
    orderId: string,
    agentId: string | null,
  ) => {
    setAgentAssignBusy(orderId);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(
        `${API_BASE}/sales/delivery-batches/assign-order-agent`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderId, agentId }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          (body as { message?: string }).message ||
            'Unable to assign shipping agent.',
        );
      }
      await load({ silent: true });
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Unable to assign shipping agent.',
      );
    } finally {
      setAgentAssignBusy(null);
    }
  };

  const unassignOrderFromBatch = async (orderId: string) => {
    setBatchAssignBusy(orderId);
    setError(null);
    try {
      const res = await globalAuthorizedFetch(`${API_BASE}/sales/delivery-batches/unassign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId }),
      });
      if (!res.ok) throw new Error('unassign');
      setBatchModalOrderId(null);
      setBatchModalPick('');
      await load({ silent: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'unassign');
    } finally {
      setBatchAssignBusy(null);
    }
  };

  const downloadOrderReceipt = async (orderId: string) => {
    try {
      setReceiptDownloadingId(orderId);
      setError(null);
      const res = await globalAuthorizedFetch(`${API_BASE}/sales/orders/${orderId}/receipt`);
      if (!res.ok) {
        throw new Error(
          t('backoffice.orders_receipt_error', { defaultValue: 'Unable to download receipt.' }),
        );
      }
      const data = (await res.json()) as {
        filename: string;
        contentType: string;
        data: string;
      };
      openPdfFromBase64Payload(data);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : t('backoffice.orders_receipt_error', { defaultValue: 'Unable to download receipt.' }),
      );
    } finally {
      setReceiptDownloadingId(null);
    }
  };

  const downloadShippingLabel = async (
    orderId: string,
    opts?: { packageIndex?: number; packageCount?: number },
  ) => {
    try {
      setShippingLabelDownloadingId(orderId);
      setError(null);
      const url = new URL(`${API_BASE}/sales/orders/${orderId}/shipping-label`);
      if (opts?.packageIndex && opts.packageIndex > 0) {
        url.searchParams.set('packageIndex', String(Math.trunc(opts.packageIndex)));
      }
      if (opts?.packageCount && opts.packageCount > 0) {
        url.searchParams.set('packageCount', String(Math.trunc(opts.packageCount)));
      }
      const res = await globalAuthorizedFetch(
        url.toString(),
      );
      if (!res.ok) {
        throw new Error(
          t('backoffice.orders_shipping_label_error', {
            defaultValue: 'Unable to download shipping label.',
          }),
        );
      }
      const data = (await res.json()) as {
        filename: string;
        contentType: string;
        data: string;
      };
      openPdfFromBase64Payload(data);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : t('backoffice.orders_shipping_label_error', {
              defaultValue: 'Unable to download shipping label.',
            }),
      );
    } finally {
      setShippingLabelDownloadingId(null);
    }
  };

  const submitFaultEmployee = async () => {
    if (!faultModalOrder) return;
    setStatusUpdating({ orderId: faultModalOrder.id, next: 'FAULT_EMPLOYEE' });
    setError(null);
    setFaultModalError(null);
    try {
      const itemAdjustments = faultModalOrder.items.map((it) => ({
        orderItemId: it.id,
        newQty: Number.parseFloat(faultAdjustments[it.id] ?? String(it.qty)),
      }));
      for (const row of itemAdjustments) {
        if (!Number.isFinite(row.newQty) || row.newQty < 0) {
          throw new Error('Invalid quantity');
        }
        const line = faultModalOrder.items.find((it) => it.id === row.orderItemId);
        if (line?.unit === 'PACK' && !Number.isInteger(row.newQty)) {
          throw new Error('Pack quantities must be whole numbers');
        }
        if (line && row.newQty >= Number(line.qty) - 1e-9) {
          throw new Error('Adjusted quantity must be less than the current line quantity');
        }
      }
      if (faultReshipStockWarnings.length > 0) {
        throw new Error(
          'Insufficient stock for reship on one or more products. Please reduce damaged quantities or restock before confirming.',
        );
      }
      const res = await globalAuthorizedFetch(
        `${API_BASE}/sales/orders/${faultModalOrder.id}/incident/resolve`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            resolution: 'FAULT_EMPLOYEE',
            itemAdjustments,
          }),
        },
      );
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error((payload as { message?: string }).message || 'Unable to resolve incident.');
      }
      setFaultModalOrder(null);
      setFaultAdjustments({});
      window.alert('Incident resolved successfully.');
      await load();
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Unable to resolve incident.';
      setError(message);
      setFaultModalError(message);
    } finally {
      setStatusUpdating(null);
    }
  };

  const refreshOrdersIfChanged = React.useCallback(async () => {
    if (refreshInFlightRef.current || document.visibilityState !== 'visible') return;
    refreshInFlightRef.current = true;
    try {
      const nextVersion = await fetchOrdersVersion();
      if (versionRef.current === null) {
        versionRef.current = nextVersion;
        return;
      }
      if (versionRef.current !== nextVersion) {
        versionRef.current = nextVersion;
        await Promise.all([
          load({ silent: true }),
          loadStats({ silent: true }),
        ]);
      }
    } finally {
      refreshInFlightRef.current = false;
    }
  }, [fetchOrdersVersion, load, loadStats]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      void refreshOrdersIfChanged();
    }, 2500);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [refreshOrdersIfChanged]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void refreshOrdersIfChanged();
      }
    };
    const onFocus = () => {
      void refreshOrdersIfChanged();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [refreshOrdersIfChanged]);

  const updateStatus = async (
    orderId: string,
    next: string,
    extraBody?: Record<string, unknown>,
  ) => {
    try {
      setStatusUpdating({ orderId, next });
      const res = await globalAuthorizedFetch(`${API_BASE}/sales/orders/${orderId}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: next, ...(extraBody || {}) }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).message || 'Unable to update status.');
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to update status.');
    } finally {
      setStatusUpdating(null);
    }
  };

  const verifyStripePaymentForOrder = async (orderId: string) => {
    const trimmed = stripePiDraft[orderId]?.trim();
    try {
      setStatusUpdating({ orderId, next: 'VERIFY_STRIPE' });
      setError(null);
      const res = await globalAuthorizedFetch(`${API_BASE}/payments/staff/orders/${orderId}/verify-stripe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(trimmed ? { paymentIntentId: trimmed } : {}),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message || 'Stripe verification failed.');
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Stripe verification failed.');
    } finally {
      setStatusUpdating(null);
    }
  };

  const postOrderAction = async (orderId: string, path: string, body?: Record<string, unknown>) => {
    try {
      setStatusUpdating({ orderId, next: path.toUpperCase() });
      const res = await globalAuthorizedFetch(`${API_BASE}/sales/orders/${orderId}/${path}`, {
        method: 'POST',
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error((payload as any).message || 'Unable to update order incident.');
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to update order incident.');
    } finally {
      setStatusUpdating(null);
    }
  };

  const postRefundRequestAction = async (
    orderId: string,
    requestId: string,
    path: 'review' | 'receive-return' | 'inspect-return',
    body?: Record<string, unknown>,
  ) => {
    try {
      setStatusUpdating({ orderId, next: `REFUND_${path.toUpperCase()}` });
      const res = await globalAuthorizedFetch(
        `${API_BASE}/sales/refund-requests/${requestId}/${path}`,
        {
          method: 'POST',
          headers: body ? { 'Content-Type': 'application/json' } : undefined,
          body: body ? JSON.stringify(body) : undefined,
        },
      );
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(
          (payload as { message?: string }).message ||
            'Unable to process refund request action.',
        );
      }
      await load();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Unable to process refund request action.',
      );
    } finally {
      setStatusUpdating(null);
    }
  };

  const openActionConfirm = React.useCallback(
    (title: string, message: string, confirmLabel: string, onConfirm: () => void) => {
      actionConfirmRef.current = onConfirm;
      setActionConfirm({ title, message, confirmLabel });
    },
    [],
  );

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchQuery.trim()) count += 1;
    if (statusFilter) count += 1;
    if (countryFilter) count += 1;
    if (fromDate) count += 1;
    if (toDate) count += 1;
    return count;
  }, [searchQuery, statusFilter, countryFilter, fromDate, toDate]);

  const visibleOrders = useMemo(
    () => orders.filter((order) => matchesOrderView(order, orderView)),
    [orders, orderView],
  );

  const totalPages = Math.max(1, Math.ceil(visibleOrders.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedOrders = useMemo(
    () => visibleOrders.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [visibleOrders, safePage],
  );

  const showBatchFilter = canDeliveryBatch
    && visibleOrders.some((order) => progressDisplayStatus(order) === 'PAID');

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath={`${location.pathname}${location.search}`}
    >
      <main className="workspace-page">
        <section className="space-y-4">
          <div>
            <h1 className="workspace-page-title">
              {t('backoffice.orders_title', { defaultValue: 'Orders' })}
            </h1>
            <p className="workspace-page-subtitle text-slate-600">
              {t('backoffice.orders_subtitle', { defaultValue: 'Manage ongoing customer orders and update their status.' })}
            </p>
          </div>

          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-3 p-4">
              <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                  <div
                    className="inline-flex max-w-full flex-wrap rounded-lg border border-slate-200 bg-slate-50 p-0.5"
                    role="tablist"
                    aria-label={t('backoffice.orders_view_scope', { defaultValue: 'Order view' })}
                  >
                    {([
                      { id: 'ACTIVE', label: t('backoffice.orders_view_active', { defaultValue: 'Processing' }) },
                      { id: 'INCIDENTS', label: t('backoffice.orders_view_incidents', { defaultValue: 'Incidents' }) },
                      { id: 'RESOLUTION', label: t('backoffice.orders_view_resolution', { defaultValue: 'Conflict resolution' }) },
                      { id: 'REFUND_QUEUE', label: t('backoffice.orders_view_refund_queue', { defaultValue: 'Refund queue' }) },
                      { id: 'ARCHIVE', label: t('backoffice.orders_view_archive', { defaultValue: 'Archive' }) },
                      { id: 'ALL', label: t('common.all', { defaultValue: 'All' }) },
                    ] as const).map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        role="tab"
                        aria-selected={orderView === opt.id}
                        onClick={() => setOrderView(opt.id)}
                        className={[
                          'rounded-md px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wide transition-colors',
                          orderView === opt.id ? 'bg-white text-brand-700 shadow-sm ring-1 ring-slate-200/80' : 'text-slate-600 hover:text-slate-900',
                        ].join(' ')}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs font-medium text-slate-500">
                    {loading
                      ? t('common.loading', { defaultValue: 'Loading…' })
                      : t('backoffice.orders_visible_count', {
                          count: visibleOrders.length,
                          defaultValue_one: '{{count}} order',
                          defaultValue_other: '{{count}} orders',
                        })}
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setFiltersOpen((open) => !open)}
                    aria-expanded={filtersOpen}
                    className={[
                      'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors',
                      filtersOpen || activeFilterCount > 0
                        ? 'border-brand-300 bg-brand-50 text-brand-800'
                        : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300',
                    ].join(' ')}
                  >
                    <Filter className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
                    {t('backoffice.orders_filters_toggle', { defaultValue: 'Filters' })}
                    {activeFilterCount > 0 ? (
                      <span className="inline-flex min-w-[1.1rem] items-center justify-center rounded-full bg-brand-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                        {activeFilterCount}
                      </span>
                    ) : null}
                    {filtersOpen ? (
                      <ChevronUp className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
                    ) : (
                      <ChevronDown className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
                    )}
                  </button>
                  {canViewStats && stats ? (
                    <button
                      type="button"
                      onClick={() => setStatsOpen((open) => !open)}
                      aria-expanded={statsOpen}
                      className={[
                        'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors',
                        statsOpen
                          ? 'border-slate-300 bg-slate-50 text-slate-900'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300',
                      ].join(' ')}
                    >
                      <BarChart3 className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
                      {t('backoffice.orders_stats_toggle', { defaultValue: 'Statistics' })}
                      {statsOpen ? (
                        <ChevronUp className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
                      )}
                    </button>
                  ) : null}
                </div>
              </div>

              {filtersOpen ? (
                <div className="flex flex-col gap-2 border-t border-slate-100 pt-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    {t('backoffice.orders_filters_label', { defaultValue: 'Search & filters' })}
                  </div>
                  <div className="flex flex-wrap items-stretch gap-2 sm:items-center">
                    <label className="sr-only" htmlFor="orders-search">
                      {t('backoffice.orders_search_placeholder', { defaultValue: 'Search name / phone / order' })}
                    </label>
                    <input
                      id="orders-search"
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t('backoffice.orders_search_placeholder', { defaultValue: 'Search name / phone / order' })}
                      className="min-w-[12rem] flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-900 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/40 sm:max-w-md"
                    />
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-xs font-semibold text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/40 sm:w-auto"
                    >
                      <option value="">{t('common.all', { defaultValue: 'All' })}</option>
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    <select
                      value={countryFilter}
                      onChange={(e) => setCountryFilter(e.target.value)}
                      className="w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-xs font-semibold text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/40 sm:max-w-[11rem]"
                    >
                      <option value="">{t('backoffice.country_all', { defaultValue: 'All countries' })}</option>
                      {stats
                        ? Object.keys(stats.byCountry)
                          .sort((a, b) => a.localeCompare(b))
                          .map((c) => <option key={c} value={c}>{c}</option>)
                        : null}
                    </select>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        {t('common.from', { defaultValue: 'From' })}
                      </label>
                      <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-semibold text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/40"
                      />
                      <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        {t('common.to', { defaultValue: 'To' })}
                      </label>
                      <input
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-semibold text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-400/40"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setFromDate('');
                        setToDate('');
                        setSearchQuery('');
                        setCountryFilter('');
                        setStatusFilter('');
                      }}
                      className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-700 hover:border-slate-300 hover:bg-white"
                    >
                      {t('common.clear', { defaultValue: 'Clear' })}
                    </button>
                  </div>
                  {showBatchFilter ? (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <label className="block text-[11px] font-medium text-slate-600">
                        {t('backoffice.orders_batch_filter_batches')}
                        <input
                          type="search"
                          value={batchListFilter}
                          onChange={(e) => setBatchListFilter(e.target.value)}
                          className="mt-1 w-full max-w-md rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm"
                          placeholder={t('backoffice.orders_batch_filter_placeholder')}
                        />
                      </label>
                      <p className="mt-1 text-[10px] text-slate-500">
                        <Link
                          to="/workspace/delivery-batches"
                          className="font-medium text-brand-700 underline"
                        >
                          {t('backoffice.orders_batch_view_batches')}
                        </Link>
                      </p>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {statsOpen && canViewStats && stats ? (
                <div className="space-y-3 border-t border-slate-100 pt-3">
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
                    {[
                      { label: t('backoffice.orders_total', { defaultValue: 'Total' }), value: stats.total },
                      { label: t('backoffice.orders_view_active', { defaultValue: 'Processing' }), value: stats.groups.processing },
                      { label: t('backoffice.orders_view_incidents', { defaultValue: 'Incidents' }), value: stats.groups.incidents },
                      { label: t('backoffice.orders_view_resolution', { defaultValue: 'Conflict resolution' }), value: stats.groups.conflictResolution },
                      { label: t('backoffice.orders_view_archive', { defaultValue: 'Archive' }), value: stats.groups.archive },
                      {
                        label: t('backoffice.orders_preorder_open', { defaultValue: 'Pre-order open' }),
                        value: stats.preorders?.open ?? 0,
                      },
                      {
                        label: t('backoffice.orders_preorder_awaiting_release', { defaultValue: 'Awaiting release' }),
                        value: stats.preorders?.awaitingRelease ?? 0,
                      },
                      {
                        label: t('backoffice.orders_preorder_balance_due', { defaultValue: 'Balance due' }),
                        value: stats.preorders?.balanceDue ?? 0,
                      },
                    ].map((card) => (
                      <div key={card.label} className="rounded-xl border border-slate-200 bg-white/80 px-3 py-2">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{card.label}</div>
                        <div className="text-xl font-semibold text-slate-900">{card.value}</div>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div className="rounded-xl border border-indigo-200 bg-indigo-50/70 px-3 py-2">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-indigo-700">
                        {t('backoffice.orders_preorder_deposit_collected', { defaultValue: 'Pre-order deposits collected' })}
                      </div>
                      <div className="text-xl font-semibold text-indigo-900">
                        {formatPrice(Number(stats.preorders?.depositCollectedUsd ?? 0))}
                      </div>
                    </div>
                    <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-3 py-2">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                        {t('backoffice.orders_preorder_remaining_balance', { defaultValue: 'Pre-order remaining balance' })}
                      </div>
                      <div className="text-xl font-semibold text-amber-900">
                        {formatPrice(Number(stats.preorders?.remainingBalanceUsd ?? 0))}
                      </div>
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white/80 px-3 py-2">
                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      {t('backoffice.orders_by_status', { defaultValue: 'By status' })}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(stats.byStatus).map(([status, count]) => (
                        <button
                          key={status}
                          type="button"
                          onClick={() => {
                            setStatusFilter(status);
                            setOrderView('ALL');
                            setFiltersOpen(true);
                          }}
                          className={[
                            'rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide',
                            statusFilter === status
                              ? 'border-brand-500 bg-brand-50 text-brand-800'
                              : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400',
                          ].join(' ')}
                        >
                          {status}: {count}
                        </button>
                      ))}
                      {statusFilter ? (
                        <button
                          type="button"
                          onClick={() => setStatusFilter('')}
                          className="rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700 hover:border-slate-400"
                        >
                          {t('common.clear', { defaultValue: 'Clear' })}
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white/80 px-3 py-2">
                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      {t('backoffice.orders_by_country', { defaultValue: 'By country' })}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(stats.byCountry).map(([country, count]) => (
                        <button
                          key={country}
                          type="button"
                          onClick={() => {
                            setCountryFilter(country);
                            setFiltersOpen(true);
                          }}
                          className={[
                            'rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-wide',
                            countryFilter === country
                              ? 'border-brand-500 bg-brand-50 text-brand-800'
                              : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400',
                          ].join(' ')}
                        >
                          {country}: {count}
                        </button>
                      ))}
                      {countryFilter ? (
                        <button
                          type="button"
                          onClick={() => setCountryFilter('')}
                          className="rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[11px] font-semibold tracking-wide text-slate-700 hover:border-slate-400"
                        >
                          {t('common.clear', { defaultValue: 'Clear' })}
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </section>

        {error && <p className="text-xs text-red-500">{error}</p>}

        <section className="min-w-0 max-w-full">
          {loading ? (
            <PaginatedListLoading title={t('common.list_loading_title')} skeletonRows={4} />
          ) : visibleOrders.length === 0 ? (
            <p className="text-sm text-slate-600">{t('backoffice.orders_empty', { defaultValue: 'No orders found.' })}</p>
          ) : (
            <div className="space-y-3">
              {pagedOrders.map((o) => {
                const workflowFrozen = Boolean(o.customerAccountDetachedAt);
                const isArchivedOrder = o.status === 'CLOSED' || o.status === 'CANCELLED';
                const isArchivedExpanded = !!expandedArchivedOrderIds[o.id];
                const showOrderBody = !isArchivedOrder || isArchivedExpanded;
                const itemsExpanded = Boolean(expandedItemsByOrder[o.id]);
                const displayStatus = progressDisplayStatus(o);
                const progressOrder = displayStatus === o.status ? o : { ...o, status: displayStatus };
                const preorderLastBal = preorderLastBalancePayment(o);
                const preorderFinalDepositSummary =
                  preorderRemainingUsdNumber(o) < 1e-9 && Boolean(preorderLastBal);
                const preorderPaymentsOpen = preorderPaymentsExpanded[o.id] ?? false;
                const isCarrierOrder =
                  USE_CARRIER_FULFILLMENT &&
                  !o.isPreorderOrder &&
                  !shouldShowIncidentOrderProgress(progressOrder);
                return (
                <div
                  key={o.id}
                  className={[
                    'rounded-lg border-2 p-3 shadow-sm',
                    isCarrierOrder ? `border-l-4 ${orderStatusAccentClass(displayStatus)}` : '',
                    workflowFrozen
                      ? 'border-amber-400 bg-white ring-1 ring-amber-200/60'
                      : o.isPreorderOrder
                        ? 'border-indigo-300 bg-indigo-50/30 ring-1 ring-indigo-200/50'
                        : 'border-slate-300 bg-white ring-1 ring-slate-200/70',
                  ].join(' ')}
                >
                  {workflowFrozen ? (
                    <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950">
                      <span className="font-semibold">
                        {t('backoffice.orders_customer_account_deleted_badge')}
                      </span>
                      {' — '}
                      {t('backoffice.orders_customer_account_deleted_banner')}
                    </div>
                  ) : null}
                  <div className={isCarrierOrder ? 'mb-1.5' : 'mb-2'}>
                    {shouldShowIncidentOrderProgress(progressOrder) ? (
                      <IncidentOrderProgressDiagram
                        order={progressOrder}
                        t={t}
                        resolvedDeliveredAt={getDeliveredAtFromOrder(o)}
                      />
                    ) : shouldShowPreorderOrderProgress(o) ? (
                      <PreorderOrderProgress
                        status={o.status}
                        t={t}
                        resolvedDeliveredAt={getDeliveredAtFromOrder(o)}
                      />
                    ) : isCarrierOrder ? (
                      <SimpleOrderProgress
                        status={displayStatus}
                        t={t}
                        resolvedDeliveredAt={getDeliveredAtFromOrder(o)}
                        compact
                      />
                    ) : (
                      <SimpleOrderProgress
                        status={displayStatus}
                        t={t}
                        resolvedDeliveredAt={getDeliveredAtFromOrder(o)}
                      />
                    )}
                  </div>
                  <div className={['flex flex-col', isCarrierOrder ? 'gap-1' : 'gap-2 sm:flex-row sm:items-start sm:justify-between'].join(' ')}>
                    <div className="min-w-0 flex-1">
                      <div className={isCarrierOrder ? '' : 'mb-2'}>
                        {o.isPreorderOrder ? (
                          <div className="flex flex-col gap-3">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                              <div className="min-w-0 flex-1">
                                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                  {t('backoffice.orders_col_order_id')}
                                </div>
                                <div
                                  className="mt-0.5 w-full min-w-0 max-w-full overflow-x-auto overscroll-x-contain pb-0.5 [-webkit-overflow-scrolling:touch] [scrollbar-width:thin]"
                                  title={formatOrderReference(o.id)}
                                >
                                  <div className="inline-block whitespace-nowrap font-mono text-lg font-bold leading-none tracking-tight text-slate-900 sm:text-xl">
                                    {formatOrderReference(o.id)}
                                  </div>
                                </div>
                                <div className="mt-2">
                                  {!USE_CARRIER_FULFILLMENT ? (
                                  <span
                                    className={[
                                      'inline-flex items-center rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-wide',
                                      o.deliverySpeed === 'EXPRESS'
                                        ? 'border border-amber-400 bg-amber-100 text-amber-950'
                                        : 'border border-slate-400 bg-slate-200 text-slate-900',
                                    ].join(' ')}
                                  >
                                    {t('backoffice.orders_delivery_speed')}:{' '}
                                    {o.deliverySpeed === 'EXPRESS' ? 'Express' : 'Standard'}
                                  </span>
                                  ) : null}
                                </div>
                              </div>
                              <div className="shrink-0 text-right sm:min-w-[11rem]">
                                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                  {t('backoffice.orders_col_total', { defaultValue: 'Total' })}
                                </div>
                                <div className="mt-0.5 text-xl font-bold tabular-nums leading-none text-slate-900 sm:text-2xl">
                                  {formatPrice(Number(o.totalUsd))}
                                </div>
                                <div className="mt-1 space-y-0.5 text-right text-[10px] font-medium text-slate-600">
                                  <div>
                                    {t('backoffice.orders_products_total', { defaultValue: 'Products total' })}:{' '}
                                    <span className="font-semibold tabular-nums text-slate-700">
                                      {formatPrice(Number(o.subtotalUsd || 0))}
                                    </span>
                                  </div>
                                  <div>
                                    {t('backoffice.orders_delivery_fee_total', { defaultValue: 'Delivery fees' })}:{' '}
                                    <span className="font-semibold tabular-nums text-slate-700">
                                      {formatPrice(Number(o.deliveryFeeUsd || 0))}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="w-full rounded-xl border border-indigo-200/60 bg-indigo-50/40 px-3 py-2.5 sm:px-4">
                              <div className="flex flex-col gap-2 border-b border-indigo-200/50 pb-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-4 sm:gap-y-1">
                                <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                                  <span className="shrink-0 rounded-md bg-indigo-600/90 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
                                    {t('backoffice.orders_preorder_payment_summary', {
                                      defaultValue: 'Pre-order',
                                    })}
                                  </span>
                                  <span className="hidden text-slate-300 sm:inline" aria-hidden>
                                    |
                                  </span>
                                  <span className="text-[11px] text-slate-600">
                                    {t('backoffice.orders_preorder_deposit_paid', {
                                      defaultValue: 'Deposit collected',
                                    })}{' '}
                                    <span className="font-extrabold tabular-nums text-slate-900">
                                      {formatPrice(Number(o.preorderDepositUsd ?? 0))}
                                    </span>
                                  </span>
                                  <span className="text-slate-300">·</span>
                                  {preorderFinalDepositSummary && preorderLastBal ? (
                                    <span className="text-[11px] text-slate-600">
                                      {t('backoffice.orders_preorder_final_deposit', {
                                        defaultValue: 'Final deposit',
                                      })}{' '}
                                      <span className="font-extrabold tabular-nums text-slate-900">
                                        {formatMajorInCurrency(
                                          preorderLastBal.amountMinor / 100,
                                          (preorderLastBal.currency || 'USD').toUpperCase(),
                                        )}
                                      </span>
                                    </span>
                                  ) : (
                                    <span className="text-[11px] text-slate-600">
                                      {t('backoffice.orders_preorder_remaining', {
                                        defaultValue: 'Remaining balance',
                                      })}{' '}
                                      <span className="font-extrabold tabular-nums text-slate-900">
                                        {formatPrice(preorderRemainingUsdNumber(o))}
                                      </span>
                                    </span>
                                  )}
                                  <span className="text-slate-300">·</span>
                                  <span className="text-[11px] text-slate-600">
                                    {t('backoffice.orders_col_total', { defaultValue: 'Order total' })}{' '}
                                    <span className="font-extrabold tabular-nums text-slate-900">
                                      {formatPrice(Number(o.totalUsd))}
                                    </span>
                                  </span>
                                </div>
                                <p className="max-w-full text-[9px] leading-snug text-slate-600 sm:max-w-xl sm:text-right sm:text-[10px]">
                                  {o.status === 'PREORDER_AWAITING_RELEASE'
                                    ? t('backoffice.orders_preorder_hint_awaiting_release', {
                                        defaultValue:
                                          'Only the deposit is collected until you release the pre-order and the customer pays the balance.',
                                      })
                                    : o.status === 'PREORDER_BALANCE_DUE'
                                      ? t('backoffice.orders_preorder_hint_balance_due', {
                                          defaultValue:
                                            'The customer still owes the remaining balance before you can ship.',
                                        })
                                      : t('backoffice.orders_preorder_hint_fulfilling', {
                                          defaultValue:
                                            'After full payment, this order follows the normal ship → deliver flow.',
                                        })}
                                </p>
                              </div>

                              <div className="pt-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setPreorderPaymentsExpanded((prev) => ({
                                      ...prev,
                                      [o.id]: !(prev[o.id] ?? false),
                                    }))
                                  }
                                  className="flex w-full items-center justify-between gap-2 rounded-lg border border-indigo-200/40 bg-white/40 px-2 py-1.5 text-left transition hover:bg-white/70"
                                  aria-expanded={preorderPaymentsOpen}
                                >
                                  <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-indigo-950">
                                    {t('backoffice.orders_preorder_card_payments_heading', {
                                      defaultValue: 'Card payment records',
                                    })}
                                  </span>
                                  {preorderPaymentsOpen ? (
                                    <ChevronDown className="h-4 w-4 shrink-0 text-indigo-800" aria-hidden />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 shrink-0 text-indigo-800" aria-hidden />
                                  )}
                                </button>
                                {preorderPaymentsOpen ? (
                                <div className="mt-1.5 overflow-x-auto rounded-lg border border-indigo-100/90 bg-white/70 [-webkit-overflow-scrolling:touch]">
                                  <table className="w-full min-w-[36rem] border-collapse text-left text-[10px]">
                                    <thead>
                                      <tr className="border-b border-slate-200/90 bg-slate-50/90 text-[9px] font-bold uppercase tracking-wide text-slate-500">
                                        <th className="whitespace-nowrap px-2 py-1.5 pr-3">
                                          {t('backoffice.orders_preorder_table_kind', { defaultValue: 'Kind' })}
                                        </th>
                                        <th className="whitespace-nowrap px-2 py-1.5 pr-3">
                                          {t('backoffice.orders_preorder_table_record', { defaultValue: 'Record' })}
                                        </th>
                                        <th className="whitespace-nowrap px-2 py-1.5 pr-3">
                                          {t('backoffice.orders_preorder_table_intent', { defaultValue: 'Intent' })}
                                        </th>
                                        <th className="whitespace-nowrap px-2 py-1.5 pr-3 text-right tabular-nums">
                                          {t('backoffice.orders_preorder_table_charged', { defaultValue: 'Charged' })}
                                        </th>
                                        <th className="whitespace-nowrap px-2 py-1.5 text-right">
                                          {t('backoffice.orders_preorder_table_status', { defaultValue: 'Status' })}
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="font-mono text-slate-800">
                                      <tr className="border-b border-slate-100 align-top">
                                        <td className="whitespace-nowrap px-2 py-1.5 pr-3 font-sans text-[9px] font-semibold uppercase text-slate-600">
                                          {t('backoffice.orders_preorder_deposit_payment', {
                                            defaultValue: 'Deposit',
                                          })}
                                        </td>
                                        <td
                                          className="max-w-[10rem] truncate px-2 py-1.5 pr-3"
                                          title={o.payment?.id ?? undefined}
                                        >
                                          {o.payment?.id ?? '—'}
                                        </td>
                                        <td
                                          className="max-w-[12rem] truncate px-2 py-1.5 pr-3"
                                          title={o.payment?.stripePaymentIntentId ?? undefined}
                                        >
                                          {o.payment?.stripePaymentIntentId ?? '—'}
                                        </td>
                                        <td className="whitespace-nowrap px-2 py-1.5 pr-3 text-right tabular-nums font-sans">
                                          {o.payment?.stripePaymentIntentId
                                            ? formatMajorInCurrency(
                                                (o.payment.amountMinor ?? 0) / 100,
                                                (o.payment.currency || 'USD').toUpperCase(),
                                              )
                                            : '—'}
                                        </td>
                                        <td className="whitespace-nowrap px-2 py-1.5 text-right font-sans text-[9px] font-semibold uppercase">
                                          {o.payment?.stripeStatus ?? '—'}
                                        </td>
                                      </tr>
                                      {(o.balancePayments?.length ?? 0) > 0
                                        ? (o.balancePayments ?? []).map((bp, idx) => (
                                            <tr
                                              key={bp.id}
                                              className="border-b border-slate-100 align-top last:border-b-0"
                                            >
                                              <td className="whitespace-nowrap px-2 py-1.5 pr-3 font-sans text-[9px] font-semibold uppercase text-slate-600">
                                                {(o.balancePayments?.length ?? 0) > 1
                                                  ? `${t('backoffice.orders_preorder_balance_payment', {
                                                      defaultValue: 'Balance',
                                                    })} ${idx + 1}`
                                                  : t('backoffice.orders_preorder_balance_payment', {
                                                      defaultValue: 'Balance',
                                                    })}
                                              </td>
                                              <td className="max-w-[10rem] truncate px-2 py-1.5 pr-3" title={bp.id}>
                                                {bp.id}
                                              </td>
                                              <td
                                                className="max-w-[12rem] truncate px-2 py-1.5 pr-3"
                                                title={bp.stripePaymentIntentId ?? undefined}
                                              >
                                                {bp.stripePaymentIntentId ?? '—'}
                                              </td>
                                              <td className="whitespace-nowrap px-2 py-1.5 pr-3 text-right tabular-nums font-sans">
                                                {formatMajorInCurrency(
                                                  bp.amountMinor / 100,
                                                  (bp.currency || 'USD').toUpperCase(),
                                                )}
                                              </td>
                                              <td className="whitespace-nowrap px-2 py-1.5 text-right font-sans text-[9px] font-semibold uppercase">
                                                {bp.stripeStatus}
                                              </td>
                                            </tr>
                                          ))
                                        : (
                                            <tr>
                                              <td
                                                colSpan={5}
                                                className="px-2 py-2 font-sans text-[10px] leading-snug text-slate-600"
                                              >
                                                {t('backoffice.orders_preorder_balance_payment_pending', {
                                                  defaultValue:
                                                    'No balance charge yet. The customer pays the remainder after you release the pre-order.',
                                                })}
                                              </td>
                                            </tr>
                                          )}
                                    </tbody>
                                  </table>
                                </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        ) : isCarrierOrder ? (
                          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                            <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-0.5">
                              <span
                                className="font-mono text-xs font-bold leading-tight text-slate-900"
                                title={formatOrderReference(o.id)}
                              >
                                {formatOrderReference(o.id)}
                              </span>
                              {(() => {
                                const deliveredAt = getDeliveredAtFromOrder(o);
                                return (
                                  <>
                                    <span className="text-[10px] text-slate-600">
                                      <span className="font-semibold text-slate-700">
                                        {t('backoffice.orders_ordered_date', { defaultValue: 'Ordered' })}:
                                      </span>{' '}
                                      {new Date(o.createdAt).toLocaleString()}
                                    </span>
                                    <span className="text-[10px] text-slate-600">
                                      <span className="font-semibold text-slate-700">
                                        {t('backoffice.orders_processing_time', { defaultValue: 'Processing' })}:
                                      </span>{' '}
                                      {formatProcessingTime(o.createdAt, deliveredAt)}
                                    </span>
                                    {o.deliveryBatch ? (
                                      <span className="text-[10px] text-slate-600">
                                        <span className="font-semibold text-slate-700">Batch:</span>{' '}
                                        <Link
                                          to="/workspace/delivery-batches"
                                          state={{ focusBatchId: o.deliveryBatch.id }}
                                          className="font-medium text-emerald-700 underline"
                                        >
                                          {o.deliveryBatch.name || o.deliveryBatch.areaLabel}
                                        </Link>
                                      </span>
                                    ) : null}
                                  </>
                                );
                              })()}
                            </div>
                            <div className="shrink-0 text-right">
                              <div className="text-base font-bold tabular-nums leading-none text-slate-900">
                                {formatPrice(Number(o.totalUsd))}
                              </div>
                              <div className="text-[9px] text-slate-500">
                                {formatPrice(Number(o.subtotalUsd || 0))} +{' '}
                                {formatPrice(Number(o.deliveryFeeUsd || 0))} ship
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                {t('backoffice.orders_col_order_id')}
                              </div>
                              <div
                                className="mt-0.5 overflow-x-auto font-mono text-base font-bold leading-tight text-slate-900 sm:text-lg"
                                title={formatOrderReference(o.id)}
                              >
                                {formatOrderReference(o.id)}
                              </div>
                              {!USE_CARRIER_FULFILLMENT ? (
                              <div className="mt-1.5">
                                <span
                                  className={[
                                    'inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase',
                                    o.deliverySpeed === 'EXPRESS'
                                      ? 'border border-amber-400 bg-amber-100 text-amber-950'
                                      : 'border border-slate-400 bg-slate-200 text-slate-900',
                                  ].join(' ')}
                                >
                                  {o.deliverySpeed === 'EXPRESS' ? 'Express' : 'Standard'}
                                </span>
                              </div>
                              ) : null}
                            </div>
                            <div className="shrink-0 text-right">
                              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                {t('backoffice.orders_col_total', { defaultValue: 'Total' })}
                              </div>
                              <div className="text-lg font-bold tabular-nums text-slate-900">
                                {formatPrice(Number(o.totalUsd))}
                              </div>
                              <div className="mt-0.5 text-[10px] text-slate-600">
                                {formatPrice(Number(o.subtotalUsd || 0))} products ·{' '}
                                {formatPrice(Number(o.deliveryFeeUsd || 0))} shipping
                              </div>
                            </div>
                          </div>
                        )}
                        {o.isPreorderOrder || isCarrierOrder ? null : (
                          <div className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                            {t('backoffice.payments_col_payment_id')}:
                            <span className="ml-1 font-mono text-xs font-semibold normal-case tracking-normal text-slate-700">
                              {o.payment?.id || '—'}
                            </span>
                          </div>
                        )}
                      </div>
                      {!isCarrierOrder ? (
                      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-600">
                        {(() => {
                          const deliveredAt = getDeliveredAtFromOrder(o);
                          return (
                            <>
                              <span>
                                <span className="font-semibold text-slate-700">
                                  {t('backoffice.orders_ordered_date', { defaultValue: 'Ordered' })}:
                                </span>{' '}
                                {new Date(o.createdAt).toLocaleString()}
                              </span>
                              <span>
                                <span className="font-semibold text-slate-700">
                                  {t('backoffice.orders_processing_time', { defaultValue: 'Processing' })}:
                                </span>{' '}
                                {formatProcessingTime(o.createdAt, deliveredAt)}
                              </span>
                              {o.deliveryBatch ? (
                                <span>
                                  <span className="font-semibold text-slate-700">Batch:</span>{' '}
                                  <Link
                                    to="/workspace/delivery-batches"
                                    state={{ focusBatchId: o.deliveryBatch.id }}
                                    className="font-medium text-emerald-700 underline"
                                  >
                                    {o.deliveryBatch.name || o.deliveryBatch.areaLabel}
                                  </Link>
                                </span>
                              ) : null}
                            </>
                          );
                        })()}
                      </div>
                      ) : null}
                      <div className={['flex flex-wrap items-center gap-1.5 text-slate-600', isCarrierOrder ? 'mt-1 text-[10px]' : 'mt-2 text-[11px]'].join(' ')}>
                        {workflowFrozen ? (
                          <span
                            className="rounded-full border border-amber-300 bg-amber-100/90 px-2 py-0.5 font-semibold uppercase text-amber-950"
                            title={t('backoffice.orders_workflow_frozen_short')}
                          >
                            {t('backoffice.orders_workflow_frozen_short')}
                          </span>
                        ) : null}
                        {o.addressVerification?.status &&
                        o.addressVerification.status !== 'VERIFIED' ? (
                          <span
                            className={[
                              'rounded-full px-2 py-0.5 font-semibold uppercase',
                              o.addressVerification.status === 'FAILED'
                                ? 'bg-rose-100 text-rose-900'
                                : 'bg-amber-100 text-amber-900',
                            ].join(' ')}
                            title={o.addressVerification.error || undefined}
                          >
                            Address {o.addressVerification.status}
                          </span>
                        ) : null}
                        {o.deliveryBatch && !isCarrierOrder ? (
                          <span className="inline-flex flex-nowrap items-center gap-1.5">
                            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-900">
                              <Truck className="h-3.5 w-3.5 shrink-0" aria-hidden />
                              <span className="text-slate-600">
                                {t('backoffice.orders_batch_label')}:
                              </span>
                              <Link
                                to="/workspace/delivery-batches"
                                state={{ focusBatchId: o.deliveryBatch.id }}
                                className="font-semibold text-brand-800 underline"
                              >
                                {o.deliveryBatch.name || o.deliveryBatch.areaLabel}
                              </Link>
                            </span>
                          </span>
                        ) : null}
                        {!USE_CARRIER_FULFILLMENT ? (
                          <>
                        {o.customer?.customerProfile?.drivingDurationSeconds != null &&
                        o.customer.customerProfile.drivingDurationSeconds > 0 ? (
                          <span
                            className="rounded-full bg-indigo-100 px-2 py-0.5 font-semibold uppercase text-indigo-900"
                            title={t('backoffice.orders_drive_time_hint', {
                              defaultValue:
                                'Road ETA from routing API. This is a driving estimate, not guaranteed arrival time with stops.',
                            })}
                          >
                            {t('backoffice.orders_drive_time_badge', { defaultValue: 'Drive ETA' })}:{' '}
                            {formatDrivingEta(o.customer.customerProfile.drivingDurationSeconds)}
                          </span>
                        ) : (
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold uppercase text-slate-600">
                            {t('backoffice.orders_drive_time_badge', { defaultValue: 'Drive ETA' })}: —
                          </span>
                        )}
                        {o.customer?.customerProfile?.isAbroadDelivery ? (
                          <span
                            className="rounded-full bg-violet-100 px-2 py-0.5 font-semibold uppercase text-violet-900"
                            title={t('backoffice.orders_abroad_hint', {
                              defaultValue: 'Abroad delivery. Road routing may be unavailable.',
                            })}
                          >
                            {t('backoffice.orders_abroad_badge', { defaultValue: 'Abroad' })}
                          </span>
                        ) : null}
                        {o.customer?.customerProfile?.flightDurationSeconds != null &&
                        o.customer.customerProfile.flightDurationSeconds > 0 ? (
                          <span
                            className="rounded-full bg-fuchsia-100 px-2 py-0.5 font-semibold uppercase text-fuchsia-900"
                            title={t('backoffice.orders_flight_eta_hint', {
                              defaultValue:
                                'Fallback ETA estimated from flight distance + handling time when no road route exists.',
                            })}
                          >
                            {t('backoffice.orders_flight_eta_badge', { defaultValue: 'Flight ETA' })}:{' '}
                            {formatDrivingEta(o.customer.customerProfile.flightDurationSeconds)}
                          </span>
                        ) : null}
                        {o.customer?.customerProfile?.drivingDistanceMeters != null &&
                        o.customer.customerProfile.drivingDistanceMeters > 0 ? (
                          <span
                            className="rounded-full bg-cyan-100 px-2 py-0.5 font-semibold uppercase text-cyan-900"
                            title={t('backoffice.orders_road_distance_hint', {
                              defaultValue: 'Road distance from routing API (not straight-line).',
                            })}
                          >
                            {t('backoffice.orders_road_distance_badge', { defaultValue: 'Road distance' })}:{' '}
                            {metersToMiles(o.customer.customerProfile.drivingDistanceMeters)}
                          </span>
                        ) : null}
                          </>
                        ) : null}
                      </div>
                      <OrderDiscountAuditBlock order={o} formatPrice={formatPrice} t={t} />
                      {canDeliveryBatch &&
                      !USE_CARRIER_FULFILLMENT &&
                      canAssignOrderToShippingAgentStatus(displayStatus) ? (
                        <div
                          className={[
                            'mt-2 rounded-lg border border-dashed px-3 py-2',
                            workflowFrozen
                              ? 'border-slate-200 bg-slate-100/80 opacity-80'
                              : 'border-slate-200 bg-slate-50/90',
                          ].join(' ')}
                        >
                          <div className="flex items-center justify-between">
                            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                              Shipping agent
                            </div>
                            {o.assignedShippingAgent ? (
                              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-800">
                                Assigned
                              </span>
                            ) : null}
                          </div>
                          {o.assignedShippingAgent ? (
                            <p className="mt-1 text-[11px] text-slate-700">
                              Currently:{' '}
                              <span className="font-semibold">
                                {o.assignedShippingAgent.employeeProfile?.fullName ||
                                  o.assignedShippingAgent.email}
                              </span>
                            </p>
                          ) : (
                            <p className="mt-1 text-[11px] text-slate-500">
                              Pick an agent to make this order visible to them
                              on the delivery scan page (works even when the
                              order is not in a batch).
                            </p>
                          )}
                          <div className="mt-2 flex flex-wrap items-end gap-2">
                            <label className="min-w-[12rem] flex-1 text-[11px] text-slate-600">
                              Pick agent
                              <select
                                disabled={workflowFrozen || shippingAgents.length === 0}
                                className="mt-0.5 w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-60"
                                value={
                                  agentPick[o.id] ??
                                  o.assignedShippingAgentId ??
                                  ''
                                }
                                onChange={(e) =>
                                  setAgentPick((p) => ({
                                    ...p,
                                    [o.id]: e.target.value,
                                  }))
                                }
                              >
                                <option value="">
                                  {shippingAgents.length === 0
                                    ? '— No agents available —'
                                    : '— Select agent —'}
                                </option>
                                {shippingAgents.map((a) => (
                                  <option key={a.id} value={a.id}>
                                    {a.fullName
                                      ? `${a.fullName} (${a.email})`
                                      : a.email}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <button
                              type="button"
                              disabled={
                                workflowFrozen ||
                                agentAssignBusy === o.id ||
                                !(agentPick[o.id] ?? o.assignedShippingAgentId ?? '').trim() ||
                                (agentPick[o.id] ?? o.assignedShippingAgentId ?? '') ===
                                  (o.assignedShippingAgentId ?? '')
                              }
                              onClick={() =>
                                void assignOrderToAgent(
                                  o.id,
                                  agentPick[o.id] ?? '',
                                )
                              }
                              className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                            >
                              {agentAssignBusy === o.id ? '…' : 'Assign'}
                            </button>
                            {o.assignedShippingAgent ? (
                              <button
                                type="button"
                                disabled={workflowFrozen || agentAssignBusy === o.id}
                                onClick={() => void assignOrderToAgent(o.id, null)}
                                className="rounded-md border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-800 disabled:opacity-50"
                              >
                                Clear
                              </button>
                            ) : null}
                          </div>
                          {shippingAgents.length === 0 ? (
                            <p className="mt-2 text-[11px] text-slate-500">
                              No active shipping agents. Add one under{' '}
                              <span className="font-medium">
                                Users → Shipping agents
                              </span>
                              .
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    {isArchivedOrder ? (
                      <div className="flex w-full min-w-0 flex-col gap-2 sm:max-w-[min(100%,26rem)] sm:items-end sm:text-right">
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => {
                              setExpandedArchivedOrderIds((prev) => {
                                if (prev[o.id]) {
                                  const next = { ...prev };
                                  delete next[o.id];
                                  return next;
                                }
                                return { ...prev, [o.id]: true };
                              });
                            }}
                            className="rounded-full border border-slate-300 bg-white px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-700 hover:border-slate-400"
                          >
                            {isArchivedExpanded
                              ? t('backoffice.collapse', { defaultValue: 'Collapse' })
                              : t('backoffice.expand', { defaultValue: 'Expand' })}
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                  {o.customer?.customerProfile?.abroadRoutePlan?.legs &&
                  o.customer.customerProfile.abroadRoutePlan.legs.length > 0 &&
                  !USE_CARRIER_FULFILLMENT ? (
                    <div className="mt-2 w-full rounded-lg border border-violet-200 bg-violet-50/70 px-2.5 py-2">
                      <div className="mb-1 inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-violet-900">
                        <Route className="h-3 w-3" />
                        {t('backoffice.orders_abroad_route_tree', { defaultValue: 'Abroad route tree' })}
                      </div>
                      <div className="grid w-full grid-cols-3 gap-1.5 text-[11px]">
                        {o.customer.customerProfile.abroadRoutePlan.legs.map((leg, idx) => {
                          const label = leg.type === 'FLIGHT'
                            ? t('backoffice.orders_flight_leg', { defaultValue: 'Flight' })
                            : leg.type === 'DRIVE_TO_AIRPORT'
                              ? t('backoffice.orders_drive_to_airport_leg', { defaultValue: 'Drive to airport' })
                              : t('backoffice.orders_drive_to_home_leg', { defaultValue: 'Drive to destination' });
                          const isFlight = leg.type === 'FLIGHT';
                          return (
                            <span key={`${o.id}-leg-${idx}`} className="inline-flex min-w-0 flex-col gap-0.5 rounded-lg border border-violet-300/80 bg-white px-2 py-1 font-medium text-violet-900">
                              <span className="inline-flex items-center gap-1">
                                {isFlight ? (
                                  <span className="inline-flex items-center gap-0.5 text-violet-700">
                                    <PlaneTakeoff className="h-3 w-3" />
                                    <PlaneLanding className="h-3 w-3" />
                                  </span>
                                ) : (
                                  <Car className="h-3 w-3" />
                                )}
                                <span>{label}</span>
                                {leg.durationSeconds != null && leg.durationSeconds > 0 ? (
                                  <span className="text-violet-700">({formatDrivingEta(leg.durationSeconds)})</span>
                                ) : null}
                              </span>
                              <span className="text-[10px] leading-snug text-violet-700/90">
                                {leg.from || '—'} → {leg.to || '—'}
                              </span>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                  {showOrderBody ? (
                    <>
                  <div className={['border-t border-slate-100 text-xs text-slate-700', isCarrierOrder ? 'mt-2 pt-2' : 'mt-2.5 pt-2.5'].join(' ')}>
                    {isCarrierOrder ? (
                      <div className="space-y-1.5">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                          <span className="text-[9px] font-bold uppercase tracking-wide text-slate-500">
                            {t('backoffice.orders_section_fulfillment', { defaultValue: 'Fulfillment' })}
                          </span>
                          {canDeliveryBatch &&
                          !workflowFrozen &&
                          (canAssignOrderToDeliveryBatchStatus(displayStatus) || o.deliveryBatch) ? (
                            <button
                              type="button"
                              onClick={() => {
                                setBatchModalPick(o.deliveryBatch?.id ?? '');
                                setBatchModalOrderId(o.id);
                              }}
                              className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-800 hover:bg-slate-50"
                            >
                              <Truck className="h-3 w-3" aria-hidden />
                              {t('backoffice.orders_shipping_batch_btn', { defaultValue: 'Shipping batch' })}
                            </button>
                          ) : null}
                          {!workflowFrozen ? (
                            <button
                              type="button"
                              onClick={() => setCarrierModalOrderId(o.id)}
                              className="inline-flex items-center gap-1 rounded-md border border-sky-500 bg-sky-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-sky-700"
                            >
                              <Package className="h-3 w-3" aria-hidden />
                              {t('backoffice.orders_fedex_btn', { defaultValue: 'FedEx label' })}
                            </button>
                          ) : null}
                          <span className="hidden h-3 w-px bg-slate-200 sm:inline" aria-hidden />
                          <span className="text-[11px] text-slate-500">{o.items.length} items</span>
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedItemsByOrder((prev) => ({ ...prev, [o.id]: !prev[o.id] }))
                            }
                            className="inline-flex items-center gap-0.5 rounded-md border border-brand-300 bg-brand-50 px-2 py-1 text-[11px] font-medium text-brand-900"
                          >
                            {itemsExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                            {itemsExpanded ? 'Hide' : 'Items'}
                          </button>
                          {canViewDetails ? (
                            <button
                              type="button"
                              onClick={() => setExpandedOrderId((prev) => (prev === o.id ? null : o.id))}
                              className="inline-flex items-center gap-0.5 rounded-md border border-indigo-300 bg-indigo-50 px-2 py-1 text-[11px] font-medium text-indigo-900"
                            >
                              {expandedOrderId === o.id ? 'Hide addr.' : 'Address'}
                            </button>
                          ) : null}
                          {canOrderRead ? (
                            <button
                              type="button"
                              onClick={() => void downloadOrderReceipt(o.id)}
                              disabled={receiptDownloadingId === o.id}
                              className="inline-flex items-center gap-0.5 rounded-md border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-800 disabled:opacity-50"
                            >
                              <FileText className="h-3 w-3" aria-hidden />
                              Receipt
                            </button>
                          ) : null}
                        </div>
                      </div>
                    ) : (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-slate-700">
                        {t('backoffice.order_items', { defaultValue: 'Items' })} ({o.items.length})
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedItemsByOrder((prev) => ({
                            ...prev,
                            [o.id]: !prev[o.id],
                          }))
                        }
                        className={[
                          'inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
                          itemsExpanded
                            ? 'border-slate-300 bg-slate-100 text-slate-800 hover:bg-slate-200 focus-visible:ring-slate-300'
                            : 'border-brand-300 bg-brand-50 text-brand-800 hover:bg-brand-100 focus-visible:ring-brand-300',
                        ].join(' ')}
                      >
                        {itemsExpanded ? <ChevronUp className="h-3.5 w-3.5" aria-hidden /> : <ChevronDown className="h-3.5 w-3.5" aria-hidden />}
                        {itemsExpanded ? 'Hide items' : 'Show items'}
                      </button>
                      {canDeliveryBatch &&
                      !workflowFrozen &&
                      (canAssignOrderToDeliveryBatchStatus(displayStatus) || o.deliveryBatch) ? (
                        <button
                          type="button"
                          onClick={() => {
                            setBatchModalPick(o.deliveryBatch?.id ?? '');
                            setBatchModalOrderId(o.id);
                          }}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-800 transition hover:bg-slate-50"
                        >
                          <Truck className="h-3 w-3" aria-hidden />
                          {USE_CARRIER_FULFILLMENT
                            ? t('backoffice.orders_shipping_batch_btn', {
                                defaultValue: 'Shipping batch',
                              })
                            : t('backoffice.orders_batch_assign_title', {
                                defaultValue: 'Delivery batch',
                              })}
                        </button>
                      ) : null}
                      {USE_CARRIER_FULFILLMENT && !o.isPreorderOrder && !workflowFrozen ? (
                        <button
                          type="button"
                          onClick={() => setCarrierModalOrderId(o.id)}
                          className="inline-flex items-center gap-1 rounded-full border border-sky-300 bg-sky-50 px-2 py-1 text-[11px] font-medium text-sky-900 transition hover:bg-sky-100"
                        >
                          <Package className="h-3 w-3" aria-hidden />
                          {t('backoffice.orders_fedex_btn', { defaultValue: 'FedEx label' })}
                        </button>
                      ) : null}
                      {canViewDetails ? (
                        <button
                          type="button"
                          onClick={() => setExpandedOrderId((prev) => (prev === o.id ? null : o.id))}
                          className={[
                            'inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
                            expandedOrderId === o.id
                              ? 'border-indigo-300 bg-indigo-100 text-indigo-900 hover:bg-indigo-200 focus-visible:ring-indigo-300'
                              : 'border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100 focus-visible:ring-indigo-300',
                          ].join(' ')}
                        >
                          {expandedOrderId === o.id ? <ChevronUp className="h-3.5 w-3.5" aria-hidden /> : <ChevronDown className="h-3.5 w-3.5" aria-hidden />}
                          {expandedOrderId === o.id
                            ? t('backoffice.hide_details', { defaultValue: 'Hide customer & address details' })
                            : t('backoffice.view_details', { defaultValue: 'Customer & address details' })}
                        </button>
                      ) : null}
                      {canOrderRead ? (
                        <button
                          type="button"
                          onClick={() => void downloadOrderReceipt(o.id)}
                          disabled={receiptDownloadingId === o.id}
                          title={t('backoffice.orders_receipt_download', {
                            defaultValue: 'Download receipt (PDF)',
                          })}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2 py-1 text-[11px] font-medium text-slate-800 transition hover:bg-slate-50 disabled:opacity-50"
                        >
                          <FileText className="h-3 w-3" aria-hidden />
                          {receiptDownloadingId === o.id
                            ? t('common.loading', { defaultValue: '…' })
                            : t('backoffice.orders_receipt_download', {
                                defaultValue: 'Receipt PDF',
                              })}
                        </button>
                      ) : null}
                      {canOrderRead && !USE_CARRIER_FULFILLMENT ? (
                        <button
                          type="button"
                          onClick={() => {
                            const canPrintShippingLabel = ['SHIPPED', 'RESHIPPED', 'DELIVERED', 'CLOSED'].includes(
                              String(o.status || ''),
                            );
                            if (!canPrintShippingLabel) return;
                            const packageLines = o.items.filter(
                              (it) => it.unit === 'PACK' || Boolean(it.packageId),
                            );
                            const inferredCount = Math.max(
                              1,
                              packageLines.reduce((sum, it) => {
                                const q = Math.round(Number(it.qty || 0));
                                return sum + (q > 0 ? q : 1);
                              }, 0),
                            );
                            setShippingLabelModal({
                              orderId: o.id,
                              packageCount: inferredCount,
                              packageIndex: '1',
                            });
                          }}
                          disabled={
                            shippingLabelDownloadingId === o.id ||
                            !['SHIPPED', 'RESHIPPED', 'DELIVERED', 'CLOSED'].includes(
                              String(o.status || ''),
                            )
                          }
                          title={
                            ['SHIPPED', 'RESHIPPED', 'DELIVERED', 'CLOSED'].includes(
                              String(o.status || ''),
                            )
                              ? t('backoffice.orders_shipping_label_download', {
                                  defaultValue: 'Print shipping label (PDF)',
                                })
                              : 'Shipping label is available only after order is shipped'
                          }
                          className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 transition hover:bg-slate-50 disabled:opacity-50"
                        >
                          <Tag className="h-4 w-4" aria-hidden />
                          {shippingLabelDownloadingId === o.id
                            ? t('common.loading', { defaultValue: '…' })
                            : t('backoffice.orders_shipping_label_download', {
                                defaultValue: 'Shipping label',
                              })}
                        </button>
                      ) : null}
                    </div>
                    )}
                    {itemsExpanded ? (
                      <div className="mt-2 space-y-3">
                        {(() => {
                          const productLines = o.items.filter((it) => !(it.unit === 'PACK' || Boolean(it.packageId)));
                          const packageLines = o.items.filter((it) => it.unit === 'PACK' || Boolean(it.packageId));
                          const productGrandTotal = productLines.reduce((sum, it) => sum + Number(it.lineTotalUsd || 0), 0);
                          const packageGrandTotal = packageLines.reduce((sum, it) => sum + Number(it.lineTotalUsd || 0), 0);
                          const lineCode = (it: SalesOrder['items'][number]) => {
                            const reservationProductId = it.stockReservations?.[0]?.product?.id || it.stockReservations?.[0]?.productId;
                            const allocationProductId = it.allocations?.[0]?.lot?.product?.id || it.allocations?.[0]?.lot?.productId;
                            const packageProductId = it.package?.items?.[0]?.product?.id || it.package?.items?.[0]?.productId;
                            const fallback = it.packageId || reservationProductId || allocationProductId || packageProductId || it.id;
                            return String(fallback).slice(0, 8).toUpperCase();
                          };

                          return (
                            <>
                              <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                                <div className="mb-2 flex items-center justify-between gap-2">
                                  <h4 className="text-sm font-bold text-slate-900">
                                    Product lines ({productLines.length})
                                  </h4>
                                  <span className="text-xs font-semibold text-slate-700">
                                    Products total: <span className="font-extrabold text-slate-900">{formatPrice(productGrandTotal)}</span>
                                  </span>
                                </div>
                                {productLines.length === 0 ? (
                                  <p className="text-xs text-slate-500">No product lines in this order.</p>
                                ) : (
                                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                                    <table className="min-w-full text-left text-xs">
                                      <thead className="bg-slate-100 text-[11px] uppercase tracking-wide text-slate-600">
                                        <tr>
                                          <th className="px-2 py-2">Product code</th>
                                          <th className="px-2 py-2">Name</th>
                                          <th className="px-2 py-2">Department</th>
                                          <th className="px-2 py-2 text-right">Qty</th>
                                          <th className="px-2 py-2 text-right">PU</th>
                                          <th className="px-2 py-2 text-right">Discount</th>
                                          <th className="px-2 py-2 text-right">Total</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-slate-100">
                                        {productLines.map((it) => (
                                          <tr key={it.id} className="align-top">
                                            <td className="px-2 py-2 font-mono">{lineCode(it)}</td>
                                            <td className="px-2 py-2 font-semibold text-slate-900">{it.productNameSnapshot}</td>
                                            <td className="px-2 py-2">{departmentsForOrderItem(it).join(', ') || '—'}</td>
                                            <td className="px-2 py-2 text-right">{it.qty} {it.unit}</td>
                                            <td className="px-2 py-2 text-right">{formatPrice(Number(it.unitPriceUsdSnapshot || 0))}</td>
                                            <td className="px-2 py-2 text-right">
                                              {Number(it.productDiscountPercentSnapshot || 0).toFixed(0)}% ({formatPrice(Number(it.lineDiscountUsd || 0))})
                                            </td>
                                            <td className="px-2 py-2 text-right font-bold text-slate-900">{formatPrice(Number(it.lineTotalUsd || 0))}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>

                              {packageLines.length > 0 ? (
                                <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                                  <div className="mb-2 flex items-center justify-between gap-2">
                                    <h4 className="text-sm font-bold text-slate-900">
                                      Package lines ({packageLines.length})
                                    </h4>
                                    <span className="text-xs font-semibold text-slate-700">
                                      Packages total: <span className="font-extrabold text-slate-900">{formatPrice(packageGrandTotal)}</span>
                                    </span>
                                  </div>
                                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                                    <table className="min-w-full text-left text-xs">
                                      <thead className="bg-slate-100 text-[11px] uppercase tracking-wide text-slate-600">
                                        <tr>
                                          <th className="px-2 py-2">Package code</th>
                                          <th className="px-2 py-2">Name</th>
                                          <th className="px-2 py-2">Department</th>
                                          <th className="px-2 py-2 text-right">Qty</th>
                                          <th className="px-2 py-2 text-right">PU</th>
                                          <th className="px-2 py-2 text-right">Discount</th>
                                          <th className="px-2 py-2 text-right">Total</th>
                                          <th className="px-2 py-2">Details</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-slate-100">
                                        {packageLines.map((it) => {
                                          const details = packageBreakdownByItem(it);
                                          const detailRows = details.map((d) => ({
                                            ...d,
                                            qtyPerBundleLbs:
                                              Number(it.qty || 0) > 0
                                                ? d.qtyLbs / Number(it.qty || 1)
                                                : d.qtyLbs,
                                          }));
                                          return (
                                            <tr key={it.id} className="align-top">
                                              <td className="px-2 py-2 font-mono">{lineCode(it)}</td>
                                              <td className="px-2 py-2 font-semibold text-slate-900">{it.productNameSnapshot}</td>
                                              <td className="px-2 py-2">{departmentsForOrderItem(it).join(', ') || '—'}</td>
                                              <td className="px-2 py-2 text-right">{it.qty} {it.unit}</td>
                                              <td className="px-2 py-2 text-right">{formatPrice(Number(it.unitPriceUsdSnapshot || 0))}</td>
                                              <td className="px-2 py-2 text-right">
                                                {Number(it.productDiscountPercentSnapshot || 0).toFixed(0)}% ({formatPrice(Number(it.lineDiscountUsd || 0))})
                                              </td>
                                              <td className="px-2 py-2 text-right font-bold text-slate-900">{formatPrice(Number(it.lineTotalUsd || 0))}</td>
                                              <td className="px-2 py-2">
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    setPackageDetailsModal({
                                                      packageCode: lineCode(it),
                                                      packageName: it.productNameSnapshot,
                                                      departmentLabel: departmentsForOrderItem(it).join(', ') || '—',
                                                      qtyLabel: `${it.qty} ${it.unit}`,
                                                      puLabel: formatPrice(Number(it.unitPriceUsdSnapshot || 0)),
                                                      discountLabel: `${Number(it.productDiscountPercentSnapshot || 0).toFixed(0)}% (${formatPrice(Number(it.lineDiscountUsd || 0))})`,
                                                      totalLabel: formatPrice(Number(it.lineTotalUsd || 0)),
                                                      rows: detailRows,
                                                    })
                                                  }
                                                  className="rounded-full border border-indigo-300 bg-indigo-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-indigo-800 hover:border-indigo-400"
                                                >
                                                  View
                                                </button>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              ) : null}
                            </>
                          );
                        })()}
                      </div>
                    ) : null}
                  </div>

                  {canViewDetails && expandedOrderId === o.id ? (
                    <div className="mt-3 grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 sm:grid-cols-2">
                      <div>
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          {t('backoffice.order_customer', { defaultValue: 'Customer' })}
                        </div>
                        <div>{o.customer?.customerProfile?.fullName || o.addressSnapshot?.fullName || '—'}</div>
                        <div>{o.customer?.email || '—'}</div>
                        <div>{o.customer?.customerProfile?.phone || o.addressSnapshot?.phone || '—'}</div>
                      </div>
                      <div>
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          {t('backoffice.order_address', { defaultValue: 'Delivery address' })}
                        </div>
                        <div>{o.addressSnapshot?.street || o.customer?.customerProfile?.street || '—'}</div>
                        <div>
                          {o.addressSnapshot?.city || o.customer?.customerProfile?.city || '—'}
                          {' '}
                          {o.addressSnapshot?.postalCode || o.customer?.customerProfile?.postalCode || ''}
                        </div>
                        <div>{o.addressSnapshot?.country || o.customer?.customerProfile?.country || '—'}</div>
                      </div>
                      <div className="sm:col-span-2">
                        {!USE_CARRIER_FULFILLMENT ? (
                        <OrderFulfillmentPanel
                          orderId={o.id}
                          orderStatus={o.status}
                          addressVerified={o.addressVerification?.status === 'VERIFIED'}
                          carrierServiceCode={o.carrierServiceCode}
                          carrierServiceName={o.carrierServiceName}
                          estimatedWeightLbs={estimateOrderWeightLbs(o.items)}
                        />
                        ) : null}
                      </div>
                      <div className="sm:col-span-2">
                        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          {t('backoffice.order_incident', { defaultValue: 'Incident' })}
                        </div>
                        {o.incidents && o.incidents.length > 0 ? (
                          <div className="mb-3 rounded border border-amber-200 bg-amber-50 p-2">
                            <div className="font-semibold text-amber-900">
                              {o.incidents[0].reason}
                              {o.incidents[0].resolution ? ` -> ${o.incidents[0].resolution}` : ''}
                            </div>
                            <div className="text-[11px] text-amber-900/80">
                              {o.incidents[0].summary || '—'}
                            </div>
                            <div className="text-[11px] text-amber-900/70">
                              {new Date(o.incidents[0].openedAt).toLocaleString()}
                            </div>
                          </div>
                        ) : (
                          <div className="mb-3 text-[11px] text-slate-500">—</div>
                        )}

                        <div className="mb-2 mt-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          {t('backoffice.incident_losses', { defaultValue: 'Incident losses' })}
                        </div>
                        {o.losses && o.losses.length > 0 ? (
                          <>
                            <ul className="space-y-1 rounded border border-rose-200 bg-rose-50 p-2">
                              {o.losses.map((ls) => (
                                <li key={ls.id} className="text-[11px] text-rose-900">
                                  {ls.reason} · {Number(ls.quantityLbs).toFixed(2)} lb
                                  {ls.lotId ? ` · lot ${ls.lotId.slice(-6).toUpperCase()}` : ''}
                                  {ls.unitCostAtLoss != null ? ` · unit $${Number(ls.unitCostAtLoss).toFixed(2)}` : ''}
                                  {' · '}
                                  {new Date(ls.createdAt).toLocaleString()}
                                </li>
                              ))}
                            </ul>
                            <div className="mt-2 text-xs font-semibold text-rose-900">
                              {t('backoffice.loss_total', { defaultValue: 'Total loss' })}: {' '}
                              {currency}{' '}
                              {formatPrice(
                                o.losses.reduce((sum, ls) => (
                                  sum + (ls.unitCostAtLoss != null ? Number(ls.unitCostAtLoss) * Number(ls.quantityLbs) : 0)
                                ), 0),
                              )}
                            </div>
                          </>
                        ) : (
                          <div className="text-[11px] text-slate-500">—</div>
                        )}
                      </div>
                      <div className="sm:col-span-2">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          {t('backoffice.order_history', { defaultValue: 'Order history' })}
                        </div>
                        <ul className="mt-1 space-y-1">
                          {(o.events || []).map((ev) => (
                            <li key={ev.id} className="flex flex-wrap items-center gap-2">
                              <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold">
                                {ev.type}
                              </span>
                              <span>
                                {ev.fromStatus ? `${ev.fromStatus} -> ${ev.toStatus}` : ev.toStatus || '—'}
                              </span>
                              <span className="text-slate-500">{new Date(ev.createdAt).toLocaleString()}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ) : null}

                  <div className="mt-4">
                    <div className="flex flex-wrap gap-2">

                    {canIncidentManage && !workflowFrozen && (o.status === 'UNREACHABLE' || o.status === 'DAMAGED') ? (
                      <button
                        type="button"
                        onClick={() =>
                          openActionConfirm(
                            'Confirm investigating status',
                            'Move this order to INVESTIGATING?',
                            'Set investigating',
                            () => void updateStatus(o.id, 'INVESTIGATING'),
                          )
                        }
                        className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-700 hover:border-slate-400"
                      >
                        {t('backoffice.set_investigating', { defaultValue: 'Set investigating' })}
                      </button>
                    ) : null}

                    {canResolutionManage && !workflowFrozen && o.status === 'INVESTIGATING' ? (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            const init: Record<string, string> = {};
                            o.items.forEach((it) => {
                              const qty = Number(it.qty || 0);
                              const maxAllowed =
                                it.unit === 'PACK'
                                  ? Math.max(0, Math.floor(qty - 1))
                                  : Math.max(0, qty - 0.01);
                              init[it.id] =
                                it.unit === 'PACK'
                                  ? String(Math.floor(maxAllowed))
                                  : String(maxAllowed);
                            });
                            setFaultModalOrder(o);
                            setFaultAdjustments(init);
                            setFaultModalError(null);
                          }}
                          className="rounded-full border border-indigo-300 bg-indigo-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-indigo-800 hover:border-indigo-400"
                        >
                          {t('backoffice.set_fault_employee', { defaultValue: 'Set fault employee' })}
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            openActionConfirm(
                              'Confirm customer fault',
                              'Resolve this incident as customer fault?',
                              'Set fault customer',
                              () => void postOrderAction(o.id, 'incident/resolve', { resolution: 'FAULT_CUSTOMER' }),
                            )
                          }
                          className="rounded-full border border-slate-300 bg-slate-100 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-700 hover:border-slate-400"
                        >
                          {t('backoffice.set_fault_customer', { defaultValue: 'Set fault customer' })}
                        </button>
                      </>
                    ) : null}

                    {canReship && !workflowFrozen && (o.status === 'FAULT_EMPLOYEE' || o.status === 'RESHIP_PENDING' || o.status === 'INSUFFICIENT_STOCK_PENDING') ? (
                      <button
                        type="button"
                        onClick={() =>
                          openActionConfirm(
                            'Confirm reship',
                            'Set reship delivery loss and optional return-to-store fee.',
                            'Continue',
                            () =>
                              setReshipLossModal({
                                orderId: o.id,
                                deliveryLossAmountMajor: '',
                                returnToStoreFeeMajor: '0',
                                carrier: '',
                                invoiceRef: '',
                              }),
                          )
                        }
                        className="rounded-full border border-brand-300 bg-brand-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-brand-800 hover:border-brand-400"
                      >
                        {t('backoffice.reship', { defaultValue: 'Reship' })}
                      </button>
                    ) : null}
                    </div>
                  </div>

                  {canVerifyStripePayment && displayStatus === 'PENDING' ? (
                    <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/80 p-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-900">
                        {t('backoffice.verify_stripe_title', { defaultValue: 'Stripe payment verification' })}
                      </p>
                      <p className="mt-1 text-xs text-emerald-900/85">
                        {t('backoffice.verify_stripe_help', {
                          defaultValue:
                            'Calls Stripe to confirm the PaymentIntent succeeded, that metadata matches this order, and that the charged amount matches the order total. Use this when the customer paid by card—not the quick Set PAID control.',
                        })}
                      </p>
                      {o.payment?.stripePaymentIntentId ? (
                        <p className="mt-2 font-mono text-[11px] text-slate-700">
                          {t('backoffice.verify_stripe_saved_pi', { defaultValue: 'Saved intent' })}:{' '}
                          {o.payment.stripePaymentIntentId}{' '}
                          <span className="text-slate-500">({o.payment.stripeStatus})</span>
                        </p>
                      ) : null}
                      <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                        <input
                          type="text"
                          value={stripePiDraft[o.id] ?? ''}
                          onChange={(e) =>
                            setStripePiDraft((prev) => ({ ...prev, [o.id]: e.target.value }))
                          }
                          placeholder={t('backoffice.verify_stripe_pi_placeholder', {
                            defaultValue: 'Optional: pi_… from Stripe if different',
                          })}
                          className="min-w-0 flex-1 rounded-md border border-emerald-200 bg-white px-2 py-1.5 font-mono text-xs text-slate-800"
                          autoComplete="off"
                        />
                        <button
                          type="button"
                          disabled={statusUpdating?.orderId === o.id}
                          onClick={() =>
                            openActionConfirm(
                              'Confirm Stripe verification',
                              'Verify Stripe payment intent and mark this order as paid?',
                              'Verify & mark paid',
                              () => void verifyStripePaymentForOrder(o.id),
                            )
                          }
                          className="rounded-full border border-emerald-600 bg-emerald-600 px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {t('backoffice.verify_stripe_button', {
                            defaultValue: 'Verify in Stripe & mark paid',
                          })}
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {canOrderUpdate ? (
                    <div
                      className={[
                        'border-t border-slate-100',
                        isCarrierOrder
                          ? 'mt-2 flex flex-col gap-2 pt-2 sm:flex-row sm:items-end sm:justify-between'
                          : 'mt-2.5 overflow-x-auto pt-2.5',
                      ].join(' ')}
                    >
                    <div className={isCarrierOrder ? 'min-w-0 flex-1 overflow-x-auto' : undefined}>
                    <p className="mb-1 text-[9px] font-bold uppercase tracking-wide text-slate-500">
                      {t('backoffice.orders_section_status', { defaultValue: 'Update status' })}
                    </p>
                    <div className="flex min-w-max flex-nowrap gap-1.5">
                    {(['SHIPPED', 'DELIVERED', 'CLOSED', 'CANCELLED'] as const).map((s) => {
                      const allowed = ALLOWED_TRANSITIONS[displayStatus] || [];
                      const isBusyThisOrder = statusUpdating?.orderId === o.id;
                      const blockedByBatchAssignment = Boolean(o.deliveryBatch);
                      const disabled =
                        workflowFrozen ||
                        blockedByBatchAssignment ||
                        !allowed.includes(s) ||
                        isBusyThisOrder;
                      return (
                        <React.Fragment key={s}>
                          <button
                            type="button"
                            onClick={() =>
                              openActionConfirm(
                                `Confirm status change to ${s.replace('_', ' ')}`,
                                `Are you sure you want to set this order to ${s.replace('_', ' ')}?`,
                                `Set ${s.replace('_', ' ')}`,
                                () => void updateStatus(o.id, s),
                              )
                            }
                            disabled={disabled}
                            className={[
                              'whitespace-nowrap rounded-full border font-semibold uppercase tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-1',
                              isCarrierOrder ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-[11px]',
                              disabled
                                ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-500'
                                : STATUS_ACTION_STYLE[s] || 'border-slate-300 bg-white text-slate-700 hover:border-slate-400',
                            ].join(' ')}
                          >
                            {t('backoffice.set_status', { defaultValue: 'Set' })} {s.replace('_', ' ')}
                          </button>
                          {s === 'DELIVERED' &&
                          canIncidentManage &&
                          !workflowFrozen &&
                          (o.status === 'SHIPPED' || o.status === 'RESHIPPED') ? (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  const batchLabel =
                                    o.deliveryBatch?.name ||
                                    o.deliveryBatch?.areaLabel ||
                                    o.deliveryBatch?.id ||
                                    '';
                                  openActionConfirm(
                                    t('backoffice.incident_open_unreachable_confirm_title', {
                                      defaultValue: 'Confirm unreachable incident',
                                    }),
                                    o.deliveryBatch
                                      ? t('backoffice.incident_open_unreachable_confirm_message_batched', {
                                          batch: batchLabel,
                                          defaultValue:
                                            'This order is on batch "{{batch}}". It will be removed from the batch so this unreachable delivery can be handled outside the batch route. Continue?',
                                        })
                                      : t('backoffice.incident_open_unreachable_confirm_message', {
                                          defaultValue: 'Are you sure you want to mark this order as unreachable?',
                                        }),
                                    t('backoffice.mark_unreachable', { defaultValue: 'Mark unreachable' }),
                                    () => void postOrderAction(o.id, 'incident/open', { reason: 'UNREACHABLE' }),
                                  );
                                }}
                                className="whitespace-nowrap rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-800 hover:border-amber-400"
                              >
                                {t('backoffice.mark_unreachable', { defaultValue: 'Mark unreachable' })}
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const batchLabel =
                                    o.deliveryBatch?.name ||
                                    o.deliveryBatch?.areaLabel ||
                                    o.deliveryBatch?.id ||
                                    '';
                                  openActionConfirm(
                                    t('backoffice.incident_open_damaged_confirm_title', {
                                      defaultValue: 'Confirm damaged incident',
                                    }),
                                    o.deliveryBatch
                                      ? t('backoffice.incident_open_damaged_confirm_message_batched', {
                                          batch: batchLabel,
                                          defaultValue:
                                            'This order is on batch "{{batch}}". It will be removed from the batch so this damaged shipment can be handled outside the batch route. Continue?',
                                        })
                                      : t('backoffice.incident_open_damaged_confirm_message', {
                                          defaultValue: 'Are you sure you want to mark this order as damaged?',
                                        }),
                                    t('backoffice.mark_damaged', { defaultValue: 'Mark damaged' }),
                                    () => void postOrderAction(o.id, 'incident/open', { reason: 'DAMAGED' }),
                                  );
                                }}
                                className="whitespace-nowrap rounded-full border border-rose-300 bg-rose-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-rose-800 hover:border-rose-400"
                              >
                                {t('backoffice.mark_damaged', { defaultValue: 'Mark damaged' })}
                              </button>
                            </>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                    {o.status === 'PREORDER_AWAITING_RELEASE' ? (
                      <button
                          type="button"
                          disabled={
                            statusUpdating?.orderId === o.id ||
                            Boolean(o.preorderReleaseBlockedReason)
                          }
                          onClick={() =>
                            openActionConfirm(
                              t('backoffice.orders_preorder_release_confirm_title', {
                                defaultValue: 'Release pre-order',
                              }),
                              t('backoffice.orders_preorder_release_confirm_body', {
                                defaultValue:
                                  'Mark this pre-order as ready for final balance payment? The customer will be asked to pay the remaining balance.',
                              }),
                              t('backoffice.orders_preorder_release_confirm_ok', {
                                defaultValue: 'Release now',
                              }),
                              async () => {
                                const res = await globalAuthorizedFetch(
                                  `${API_BASE}/sales/orders/${o.id}/preorder/release`,
                                  { method: 'POST' },
                                );
                                if (!res.ok) {
                                  const b = await res.json().catch(() => ({}));
                                  throw new Error(
                                    (b as { message?: string }).message ||
                                      'Unable to release pre-order',
                                  );
                                }
                                await load({ silent: true });
                              },
                            )
                          }
                          className="rounded-full border border-indigo-600 bg-indigo-600 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {t('backoffice.orders_preorder_release_button', {
                            defaultValue: 'Release pre-order',
                          })}
                      </button>
                    ) : null}
                    </div>
                    </div>
                    {isCarrierOrder ? (
                      <div className="w-full shrink-0 sm:w-48">
                        <CarrierShippingBanner
                          serviceName={o.carrierServiceName}
                          serviceCode={o.carrierServiceCode}
                          t={t}
                          compact
                        />
                      </div>
                    ) : null}
                    </div>
                  ) : null}
                  {(() => {
                    const rr = (o.refundRequests || [])[0];
                    if (!rr) return null;
                    const badgeClass =
                      rr.status === 'REQUESTED'
                        ? 'bg-amber-50 text-amber-800 border-amber-200'
                        : rr.status === 'WAITING_RETURN'
                          ? 'bg-blue-50 text-blue-800 border-blue-200'
                          : rr.status === 'REFUND_PROCESSING' || rr.status === 'RETURN_RECEIVED'
                            ? 'bg-purple-50 text-purple-800 border-purple-200'
                            : rr.status === 'DONE'
                              ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                              : rr.status === 'REJECTED'
                                ? 'bg-rose-50 text-rose-800 border-rose-200'
                                : 'bg-slate-50 text-slate-700 border-slate-200';
                    const isBusyThisOrder = statusUpdating?.orderId === o.id;
                    const evidenceLinks = rr.evidences || [];
                    return (
                      <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                            Refund request
                          </p>
                          <span
                            className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${badgeClass}`}
                          >
                            {rr.status.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <div className="mt-2 grid gap-2 text-xs text-slate-700 sm:grid-cols-2">
                          <p>
                            <span className="font-semibold">Reason:</span>{' '}
                            {rr.reason.replace(/_/g, ' ')}
                          </p>
                          <p>
                            <span className="font-semibold">Requested at:</span>{' '}
                            {new Date(rr.createdAt).toLocaleString()}
                          </p>
                          {rr.requestNote ? (
                            <p className="sm:col-span-2">
                              <span className="font-semibold">Customer note:</span>{' '}
                              {rr.requestNote}
                            </p>
                          ) : null}
                          {rr.reasonText ? (
                            <p className="sm:col-span-2">
                              <span className="font-semibold">Evidence details:</span>{' '}
                              {rr.reasonText}
                            </p>
                          ) : null}
                          {rr.rejectionReason ? (
                            <p className="sm:col-span-2 text-rose-700">
                              <span className="font-semibold">Rejection reason:</span>{' '}
                              {rr.rejectionReason}
                            </p>
                          ) : null}
                        </div>
                        {evidenceLinks.length > 0 ? (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {evidenceLinks.map((ev, idx) => (
                              <a
                                key={ev.id}
                                href={ev.imageUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="rounded-full border border-slate-300 bg-white px-3 py-1 text-[11px] font-semibold text-slate-700 hover:border-slate-400"
                              >
                                Evidence {idx + 1}
                              </a>
                            ))}
                          </div>
                        ) : null}
                        {canOrderUpdate ? (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {rr.status === 'REQUESTED' ? (
                              <>
                                <button
                                  type="button"
                                  disabled={isBusyThisOrder}
                                  onClick={() =>
                                    void postRefundRequestAction(
                                      o.id,
                                      rr.id,
                                      'review',
                                      { decision: 'APPROVE' },
                                    )
                                  }
                                  className="rounded-full border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-800 hover:border-emerald-400 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  Approve request
                                </button>
                                <button
                                  type="button"
                                  disabled={isBusyThisOrder}
                                  onClick={() =>
                                    openActionConfirm(
                                      'Reject refund request',
                                      'Reject this customer refund request?',
                                      'Reject',
                                      () =>
                                        void postRefundRequestAction(
                                          o.id,
                                          rr.id,
                                          'review',
                                          {
                                            decision: 'REJECT',
                                            note: 'Rejected by staff',
                                          },
                                        ),
                                    )
                                  }
                                  className="rounded-full border border-rose-300 bg-rose-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-rose-800 hover:border-rose-400 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  Reject request
                                </button>
                              </>
                            ) : null}
                            {rr.status === 'WAITING_RETURN' ? (
                              <button
                                type="button"
                                disabled={isBusyThisOrder}
                                onClick={() =>
                                  void postRefundRequestAction(
                                    o.id,
                                    rr.id,
                                    'receive-return',
                                  )
                                }
                                className="rounded-full border border-blue-300 bg-blue-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-blue-800 hover:border-blue-400 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                Mark return received
                              </button>
                            ) : null}
                            {rr.status === 'RETURN_RECEIVED' ||
                            rr.status === 'REFUND_PROCESSING' ? (
                              <button
                                type="button"
                                disabled={isBusyThisOrder}
                                onClick={() => {
                                  const productId =
                                    o.items[0]?.stockReservations?.[0]?.productId ||
                                    o.items[0]?.allocations?.[0]?.lot?.productId ||
                                    '';
                                  if (!productId) {
                                    setError('No product found for inspection line.');
                                    return;
                                  }
                                  void postRefundRequestAction(
                                    o.id,
                                    rr.id,
                                    'inspect-return',
                                    {
                                      lines: [
                                        {
                                          productId,
                                          qtyLbs: 0.5,
                                          decision: 'RESTOCK',
                                          note: 'Auto quick inspection from staff panel',
                                        },
                                      ],
                                      note: 'Inspection completed from staff panel',
                                    },
                                  );
                                }}
                                className="rounded-full border border-purple-300 bg-purple-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-purple-800 hover:border-purple-400 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                Complete inspection
                              </button>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    );
                  })()}
                  {canOrderUpdate && o.deliveryBatch ? (
                    <p className="mt-2 text-xs font-medium text-amber-700">
                      {t('backoffice.orders_batch_status_locked', {
                        defaultValue:
                          'Status actions are locked while this order is assigned to a batch. Remove it from the batch first.',
                      })}
                    </p>
                  ) : null}
                  {statusUpdating?.orderId === o.id ? (
                    <p className="mt-2 text-xs font-medium text-brand-700">
                      {t('backoffice.setting_status', { defaultValue: 'Setting' })} {statusUpdating.next}...
                    </p>
                  ) : null}
                    </>
                  ) : (
                    <p className="mt-3 text-xs text-slate-500">
                      {t('backoffice.order_collapsed_hint', { defaultValue: 'Closed order collapsed. Expand to view details.' })}
                    </p>
                  )}
                </div>
                );
              })}
              <div className="flex flex-col gap-2 border-t border-slate-200 pt-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-slate-500">
                  {t('backoffice.orders_pagination_info', {
                    defaultValue: 'Page {{page}} of {{totalPages}}',
                    page: safePage,
                    totalPages,
                  })}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={safePage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="rounded-full border border-slate-300 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700 hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t('common.prev', { defaultValue: 'Prev' })}
                  </button>
                  <button
                    type="button"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className="rounded-full border border-slate-300 bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-700 hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t('common.next', { defaultValue: 'Next' })}
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      </main>

      {faultModalOrder ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="fault-employee-title"
        >
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
            <h2 id="fault-employee-title" className="text-base font-semibold text-slate-900">
              {t('backoffice.fault_employee_modal_title', {
                defaultValue: 'Employee fault — adjust quantities',
              })}
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              {t('backoffice.fault_employee_modal_help', {
                defaultValue:
                  'Set the quantity still in good condition for each line. Any difference is treated as damaged and will drive the reship amount. Leave lines unchanged for a full-line replacement on reship.',
              })}
            </p>
            <div className="mt-4 space-y-3">
              {faultModalOrder.items.map((it) => (
                <div key={it.id} className="flex flex-col gap-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <div className="text-xs font-semibold text-slate-800">{it.productNameSnapshot}</div>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                    <span>
                      {t('backoffice.fault_employee_kept_qty', { defaultValue: 'Good quantity' })} ({it.unit})
                    </span>
                    <input
                      type="number"
                      min={0}
                      max={
                        it.unit === 'PACK'
                          ? Math.max(0, Math.floor(Number(it.qty || 0) - 1))
                          : Math.max(0, Number(it.qty || 0) - 0.01)
                      }
                      step={it.unit === 'PACK' ? '1' : '0.01'}
                      value={faultAdjustments[it.id] ?? ''}
                      onChange={(e) =>
                        setFaultAdjustments((prev) => {
                          const nextRaw = e.target.value;
                          const sanitized =
                            it.unit === 'PACK'
                              ? nextRaw.replace(/[^\d]/g, '')
                              : nextRaw;
                          const parsed = Number.parseFloat(sanitized);
                          if (!Number.isFinite(parsed)) {
                            return { ...prev, [it.id]: sanitized };
                          }
                          const maxAllowed =
                            it.unit === 'PACK'
                              ? Math.max(0, Math.floor(Number(it.qty || 0) - 1))
                              : Math.max(0, Number(it.qty || 0) - 0.01);
                          const clamped = Math.min(Math.max(parsed, 0), maxAllowed);
                          const nextValue =
                            it.unit === 'PACK'
                              ? String(Math.floor(clamped))
                              : String(clamped);
                          return { ...prev, [it.id]: nextValue };
                        })
                      }
                      className="w-28 rounded border border-slate-300 px-2 py-1 text-sm font-semibold text-slate-900"
                    />
                    <span className="text-slate-500">
                      / {it.qty} {it.unit}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            {faultStockLoading ? (
              <p className="mt-3 text-xs text-slate-500">
                Checking remaining stock for potential reship...
              </p>
            ) : null}
            {faultReshipStockWarnings.length > 0 ? (
              <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2">
                <p className="text-xs font-semibold text-amber-900">
                  Reship stock warning while editing
                </p>
                <p className="mt-1 text-[11px] text-amber-900">
                  Not enough remaining stock for the reship quantities below:
                </p>
                <ul className="mt-1 list-inside list-disc space-y-0.5 text-[11px] text-amber-900">
                  {faultReshipStockWarnings.map((w) => (
                    <li key={`fault-stock-${w.productId}`}>
                      {w.name}: need {w.neededLbs.toFixed(2)} LB, available {w.availableLbs.toFixed(2)} LB
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {faultModalError ? (
              <p className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700">
                {faultModalError}
              </p>
            ) : null}
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setFaultModalOrder(null);
                  setFaultAdjustments({});
                  setFaultModalError(null);
                }}
                className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
              >
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button
                type="button"
                onClick={() => void submitFaultEmployee()}
                disabled={
                  Boolean(faultModalOrder.customerAccountDetachedAt) ||
                  statusUpdating?.orderId === faultModalOrder.id ||
                  faultStockLoading ||
                  faultReshipStockWarnings.length > 0
                }
                className="rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
              >
                {statusUpdating?.orderId === faultModalOrder.id
                  ? t('common.saving', { defaultValue: 'Saving…' })
                  : t('backoffice.fault_employee_confirm', { defaultValue: 'Confirm fault (employee)' })}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {actionConfirm ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/35 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="order-action-confirm-title"
        >
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
            <h3 id="order-action-confirm-title" className="text-base font-semibold text-slate-900">
              {actionConfirm.title}
            </h3>
            <p className="mt-2 text-sm text-slate-600">{actionConfirm.message}</p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  actionConfirmRef.current = null;
                  setActionConfirm(null);
                }}
                className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
              >
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button
                type="button"
                onClick={() => {
                  const run = actionConfirmRef.current;
                  actionConfirmRef.current = null;
                  setActionConfirm(null);
                  run?.();
                }}
                className="rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
              >
                {actionConfirm.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {reshipLossModal ? (
        <div className="fixed inset-0 z-[85] flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl ring-1 ring-slate-900/10">
            <h3 className="text-base font-semibold text-slate-900">Reship delivery loss</h3>
            <p className="mt-1 text-sm text-slate-600">
              Record delivery money lost for this reship. Return-to-store fee defaults to 0.
            </p>
            <div className="mt-4 grid gap-3">
              <label className="text-sm font-medium text-slate-800">
                Delivery loss amount (USD) <span className="text-red-600">*</span>
                <input
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  inputMode="decimal"
                  value={reshipLossModal.deliveryLossAmountMajor}
                  onChange={(e) =>
                    setReshipLossModal((prev) =>
                      prev ? { ...prev, deliveryLossAmountMajor: e.target.value } : prev,
                    )
                  }
                  placeholder="e.g. 8.50"
                />
              </label>
              <label className="text-sm font-medium text-slate-800">
                Return-to-store fee (USD)
                <input
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  inputMode="decimal"
                  value={reshipLossModal.returnToStoreFeeMajor}
                  onChange={(e) =>
                    setReshipLossModal((prev) =>
                      prev ? { ...prev, returnToStoreFeeMajor: e.target.value } : prev,
                    )
                  }
                />
              </label>
              <label className="text-sm font-medium text-slate-800">
                Carrier / service (optional)
                <input
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  value={reshipLossModal.carrier}
                  onChange={(e) =>
                    setReshipLossModal((prev) =>
                      prev ? { ...prev, carrier: e.target.value } : prev,
                    )
                  }
                />
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
                onClick={() => setReshipLossModal(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white"
                onClick={async () => {
                  if (!reshipLossModal) return;
                  const deliveryLoss = Number(
                    reshipLossModal.deliveryLossAmountMajor.replace(',', '.'),
                  );
                  const returnFee = Number(
                    (reshipLossModal.returnToStoreFeeMajor || '0').replace(',', '.'),
                  );
                  if (!Number.isFinite(deliveryLoss) || deliveryLoss <= 0) {
                    setError('Enter a delivery loss amount greater than zero.');
                    return;
                  }
                  if (!Number.isFinite(returnFee) || returnFee < 0) {
                    setError('Return-to-store fee must be zero or higher.');
                    return;
                  }
                  await postOrderAction(reshipLossModal.orderId, 'reship', {
                    deliveryLossAmountMinor: Math.round(deliveryLoss * 100),
                    returnToStoreFeeMinor: Math.round(returnFee * 100),
                    carrier: reshipLossModal.carrier || undefined,
                    invoiceRef: reshipLossModal.invoiceRef || undefined,
                  });
                  setReshipLossModal(null);
                }}
              >
                Confirm reship
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {batchModalOrderId ? (() => {
        const batchModalOrder = orders.find((x) => x.id === batchModalOrderId);
        if (!batchModalOrder) return null;
        const batchOptions = USE_CARRIER_FULFILLMENT
          ? filteredOpenBatches
          : openBatchesBySpeed[
              batchModalOrder.deliverySpeed === 'EXPRESS' ? 'EXPRESS' : 'STANDARD'
            ] ?? [];
        const canAssign = canAssignOrderToDeliveryBatchStatus(
          progressDisplayStatus(batchModalOrder),
        );
        const busy = batchAssignBusy === batchModalOrderId;
        return (
          <div
            className="fixed inset-0 z-[86] flex items-center justify-center bg-slate-900/45 p-4"
            role="dialog"
            aria-modal="true"
            onClick={() => !busy && setBatchModalOrderId(null)}
          >
            <div
              className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-base font-semibold text-slate-900">
                {USE_CARRIER_FULFILLMENT
                  ? t('backoffice.orders_shipping_batch_modal_title', {
                      defaultValue: 'Shipping batch',
                    })
                  : t('backoffice.orders_batch_assign_title', { defaultValue: 'Delivery batch' })}
              </h3>
              <p className="mt-1 font-mono text-xs text-slate-500">
                {formatOrderReference(batchModalOrder.id)}
              </p>
              {batchModalOrder.deliveryBatch ? (
                <p className="mt-2 text-sm text-slate-700">
                  {t('backoffice.orders_batch_current', { defaultValue: 'Currently in' })}{' '}
                  <span className="font-semibold">
                    {batchModalOrder.deliveryBatch.name || batchModalOrder.deliveryBatch.areaLabel}
                  </span>
                </p>
              ) : (
                <p className="mt-2 text-sm text-slate-600">
                  {t('backoffice.orders_batch_modal_empty', {
                    defaultValue: 'This order is not assigned to a batch yet.',
                  })}
                </p>
              )}
              {canAssign ? (
                <label className="mt-4 block text-xs font-medium text-slate-700">
                  {t('backoffice.orders_batch_pick', { defaultValue: 'Choose batch' })}
                  <select
                    disabled={busy}
                    className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm disabled:opacity-60"
                    value={batchModalPick}
                    onChange={(e) => setBatchModalPick(e.target.value)}
                  >
                    <option value="">{t('backoffice.orders_batch_pick', { defaultValue: 'Choose batch' })}</option>
                    {batchOptions.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name || b.areaLabel}
                        {b.status !== 'OPEN' ? ` (${b.status})` : ''}
                        {!USE_CARRIER_FULFILLMENT ? ` · ${b.deliverySpeed || 'STANDARD'}` : ''}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              <div className="mt-5 flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setBatchModalOrderId(null)}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50"
                >
                  {t('common.cancel', { defaultValue: 'Cancel' })}
                </button>
                {batchModalOrder.deliveryBatch ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void unassignOrderFromBatch(batchModalOrderId)}
                    className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-800 disabled:opacity-50"
                  >
                    {busy
                      ? t('common.loading', { defaultValue: '…' })
                      : t('backoffice.orders_batch_unassign', { defaultValue: 'Remove from batch' })}
                  </button>
                ) : null}
                {canAssign ? (
                  <button
                    type="button"
                    disabled={busy || !batchModalPick.trim()}
                    onClick={() => void assignOrderToBatch(batchModalOrderId, batchModalPick)}
                    className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {busy
                      ? t('common.loading', { defaultValue: '…' })
                      : t('backoffice.orders_batch_assign_btn', { defaultValue: 'Assign' })}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        );
      })() : null}
      {carrierModalOrderId ? (() => {
        const carrierOrder = orders.find((x) => x.id === carrierModalOrderId);
        if (!carrierOrder) return null;
        return (
          <div
            className="fixed inset-0 z-[86] flex items-center justify-center bg-slate-900/45 p-4"
            role="dialog"
            aria-modal="true"
            onClick={() => setCarrierModalOrderId(null)}
          >
            <div
              className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-slate-200 bg-white p-4 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-3 flex items-start justify-between gap-2">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">
                    {t('backoffice.carrier_fedex_fulfillment', { defaultValue: 'FedEx shipping' })}
                  </h3>
                  <p className="font-mono text-xs text-slate-500">
                    {formatOrderReference(carrierOrder.id)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setCarrierModalOrderId(null)}
                  className="rounded-lg border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
                >
                  {t('common.close', { defaultValue: 'Close' })}
                </button>
              </div>
              <OrderFulfillmentPanel
                orderId={carrierOrder.id}
                orderStatus={carrierOrder.status}
                addressVerified={carrierOrder.addressVerification?.status === 'VERIFIED'}
                carrierServiceCode={carrierOrder.carrierServiceCode}
                carrierServiceName={carrierOrder.carrierServiceName}
                estimatedWeightLbs={estimateOrderWeightLbs(carrierOrder.items)}
                isInternational={isInternationalDestination(
                  carrierOrder.addressSnapshot?.country ||
                    carrierOrder.customer?.customerProfile?.country,
                )}
                compact
              />
            </div>
          </div>
        );
      })() : null}
      {shippingLabelModal ? (
        <div
          className="fixed inset-0 z-[86] flex items-center justify-center bg-slate-900/45 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl">
            <h3 className="text-base font-semibold text-slate-900">
              {t('backoffice.orders_shipping_label_download', {
                defaultValue: 'Shipping label',
              })}
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              Choose whether to print all parcel labels or one selected parcel.
            </p>
            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs text-slate-700">
                Estimated parcel count:{' '}
                <span className="font-semibold text-slate-900">
                  {shippingLabelModal.packageCount}
                </span>
              </p>
              <label className="mt-2 block text-xs font-medium text-slate-700">
                Parcel index to print
                <input
                  type="number"
                  min={1}
                  max={shippingLabelModal.packageCount}
                  value={shippingLabelModal.packageIndex}
                  onChange={(e) =>
                    setShippingLabelModal((prev) =>
                      prev ? { ...prev, packageIndex: e.target.value } : prev,
                    )
                  }
                  className="mt-1 w-28 rounded border border-slate-300 px-2 py-1.5 text-sm"
                />
              </label>
            </div>
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => setShippingLabelModal(null)}
                className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
              >
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button
                type="button"
                disabled={shippingLabelDownloadingId === shippingLabelModal.orderId}
                onClick={async () => {
                  const idx = Number.parseInt(shippingLabelModal.packageIndex, 10);
                  const packageIndex = Number.isFinite(idx)
                    ? Math.min(
                        shippingLabelModal.packageCount,
                        Math.max(1, Math.trunc(idx)),
                      )
                    : 1;
                  await downloadShippingLabel(shippingLabelModal.orderId, {
                    packageIndex,
                    packageCount: shippingLabelModal.packageCount,
                  });
                  setShippingLabelModal(null);
                }}
                className="rounded-full border border-indigo-300 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-800 hover:bg-indigo-100 disabled:opacity-50"
              >
                Print selected
              </button>
              <button
                type="button"
                disabled={shippingLabelDownloadingId === shippingLabelModal.orderId}
                onClick={async () => {
                  const count = shippingLabelModal.packageCount;
                  for (let i = 1; i <= count; i += 1) {
                    // Sequential print preserves deterministic package index output.
                    // eslint-disable-next-line no-await-in-loop
                    await downloadShippingLabel(shippingLabelModal.orderId, {
                      packageIndex: i,
                      packageCount: count,
                    });
                  }
                  setShippingLabelModal(null);
                }}
                className="rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                Print all
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {packageDetailsModal ? (
        <div className="fixed inset-0 z-[86] flex items-center justify-center bg-slate-900/45 p-4" role="dialog" aria-modal="true">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-slate-900">Package details</h3>
                <p className="mt-1 text-xs text-slate-600">
                  {packageDetailsModal.packageName} ({packageDetailsModal.packageCode})
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPackageDetailsModal(null)}
                className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-700"
              >
                Close
              </button>
            </div>

            <div className="mt-3 grid gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 sm:grid-cols-3">
              <p><span className="font-semibold">Department:</span> {packageDetailsModal.departmentLabel}</p>
              <p><span className="font-semibold">Quantity:</span> {packageDetailsModal.qtyLabel}</p>
              <p><span className="font-semibold">Unit price:</span> {packageDetailsModal.puLabel}</p>
              <p><span className="font-semibold">Discount:</span> {packageDetailsModal.discountLabel}</p>
              <p><span className="font-semibold">Line total:</span> {packageDetailsModal.totalLabel}</p>
            </div>

            <div className="mt-4">
              <h4 className="mb-2 text-sm font-semibold text-slate-900">Package composition</h4>
              {packageDetailsModal.rows.length > 0 ? (
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <table className="min-w-full text-left text-xs">
                    <thead className="bg-slate-100 text-[11px] uppercase tracking-wide text-slate-600">
                      <tr>
                        <th className="px-2 py-2">Item</th>
                        <th className="px-2 py-2 text-right">LB per bundle</th>
                        <th className="px-2 py-2 text-right">Total LB</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {packageDetailsModal.rows.map((row, idx) => (
                        <tr key={`pkg-modal-row-${idx}`}>
                          <td className="px-2 py-2 font-semibold text-slate-900">{row.name}</td>
                          <td className="px-2 py-2 text-right">{row.qtyPerBundleLbs.toFixed(2)}</td>
                          <td className="px-2 py-2 text-right">{row.qtyLbs.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-xs text-slate-500">Composition unavailable for this package line.</p>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </MainLayout>
  );
}

